import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Card, CardContent,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar,
  Alert, CircularProgress, alpha, useTheme, useMediaQuery, Pagination,
  Tabs, Tab, Fade, Tooltip, InputAdornment,
  Snackbar,
  FormControl, InputLabel, Select,
  Collapse, Stack
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';

// --- ICONOS ---
import {
  AttachMoney as MoneyIcon,       
  Receipt as ReceiptIcon,         
  Description as QuoteIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  MoneyOff as MoneyOffIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  Repeat as RepeatIcon,
  PlayCircleFilled as ProcessIcon,
  AttachFile as AttachFileIcon,
  CurrencyExchange as CurrencyIcon,
  AccountBalance as BankIcon,
  FilterAlt as FilterIcon,
  FilterAltOff as FilterOffIcon,
  History as HistoryIcon,
  ContentCopy as DuplicateIcon,
  SwapHoriz as ConvertIcon,
  Email as EmailIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';

import { format } from 'date-fns';
import invoiceService from '../services/invoiceService';
import projectService from '../services/projectService';
import InvoiceFormDialog from '../components/InvoiceFormDialog';
import EmailComposerDialog from '../components/EmailComposerDialog';
import QuoteCommercialDialog from '../components/QuoteCommercialDialog';
import FinanceBreakdownDialog from '../components/FinanceBreakdownDialog';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { generatePurchaseOrderPDF } from '../utils/pdfGenerator';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { downloadBlobFile, openBlobFile } from '../services/nativeFileService';
import ExpenseReceiptPicker from '../components/ExpenseReceiptPicker';
import { expenseReceiptCountError } from '../utils/expenseReceiptUploads';

const FALLBACK_STATUS_OPTIONS_BY_TYPE = {
  invoice: ['pending', 'paid', 'overdue', 'cancelled'],
  quote: ['draft', 'sent', 'approved', 'rejected', 'expired', 'pending'],
  purchase_order: ['pending', 'approved', 'in_progress', 'completed', 'cancelled', 'rejected', 'paid']
};

const FALLBACK_STATUS_LABELS_BY_TYPE = {
  invoice: {
    pending: 'Pending Payment',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    approved: 'Approved'
  },
  quote: {
    draft: 'Draft',
    sent: 'Sent to Client',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    pending: 'Pending Review'
  },
  purchase_order: {
    pending: 'Pending Approval',
    approved: 'Approved',
    in_progress: 'In Process',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rejected: 'Rejected',
    paid: 'Completed'
  }
};

const STATUS_COLORS = {
  paid: '#2e7d32',
  completed: '#2e7d32',
  approved: '#1976d2',
  pending: '#ed6c02',
  draft: '#6d4c41',
  sent: '#0288d1',
  in_progress: '#7b1fa2',
  overdue: '#d32f2f',
  rejected: '#757575',
  cancelled: '#616161',
  expired: '#8d6e63'
};

const getStatusConfigByType = (type, statusRaw, labelsByType = FALLBACK_STATUS_LABELS_BY_TYPE) => {
  const status = (statusRaw || '').toString().toLowerCase().trim() || 'pending';
  const labels = labelsByType[type] || {};
  const label = labels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const color = STATUS_COLORS[status] || '#ed6c02';
  return { label, color };
};

const getStatusFilterOptions = (tab, optionsByType = FALLBACK_STATUS_OPTIONS_BY_TYPE) => {
  if (tab === 1) return optionsByType.invoice || [];
  if (tab === 2) return optionsByType.quote || [];
  if (tab === 4) return optionsByType.purchase_order || [];
  return Array.from(new Set([
    ...(optionsByType.invoice || []),
    ...(optionsByType.quote || []),
    ...(optionsByType.purchase_order || [])
  ]));
};

const getRowTransitionStatusOptions = ({
  type,
  currentStatus,
  statusOptionsByType = FALLBACK_STATUS_OPTIONS_BY_TYPE,
  statusRules
}) => {
  const normalizedType = (type || '').toString().toLowerCase().trim();
  const normalizedCurrent = (currentStatus || '').toString().toLowerCase().trim();
  const baseOptions = statusOptionsByType[normalizedType] || FALLBACK_STATUS_OPTIONS_BY_TYPE[normalizedType] || [];
  const withCurrent = normalizedCurrent && !baseOptions.includes(normalizedCurrent)
    ? [...baseOptions, normalizedCurrent]
    : baseOptions;

  const transitions = statusRules?.[normalizedType]?.transitions;
  if (!transitions || typeof transitions !== 'object') return withCurrent;

  const allowedNext = Array.isArray(transitions[normalizedCurrent]) ? transitions[normalizedCurrent] : [];
  if (!normalizedCurrent) return withCurrent;

  const allowedSet = new Set([normalizedCurrent, ...allowedNext]);
  const filtered = withCurrent.filter((status) => allowedSet.has(status));
  return filtered.length ? filtered : withCurrent;
};

const EXPENSE_CATEGORIES = [
  'Office Supplies', 'Rent', 'Utilities', 'Salaries', 'Sales Commission', 'Software', 'Travel', 'Marketing', 'Company Loan', 'Other'
];

const TAB_VALUES = {
  allDocs: 0,
  invoices: 1,
  quotes: 2,
  expenses: 3,
  purchaseOrders: 4,
  fixedExpenses: 5,
  companyLoans: 6
};

const createEmptyPaymentForm = (type = 'invoice') => ({
  payment_direction: type === 'purchase_order' ? 'outgoing' : 'incoming',
  amount: '',
  payment_date: format(new Date(), 'yyyy-MM-dd'),
  payment_method: 'Bank transfer',
  reference: '',
  notes: '',
  idempotency_key: `${Date.now()}-${Math.random().toString(36).slice(2)}`
});

const createEmptyCurrencyStats = () => ({
  totalRevenue: 0,
  totalExpenses: 0,
  netProfit: 0,
  pendingAmount: 0,
  overdueAmount: 0,
  quotesAmount: 0,
  poAmount: 0,
  invoicesAmount: 0,
  loansAmount: 0
});

const createEmptyVatStats = () => ({
  vatCollected: 0,
  taxableCollected: 0,
  grossCollected: 0,
  documentCount: 0,
  legacyPaidCount: 0
});

const createEmptyFinanceStats = () => ({
  MXN: createEmptyCurrencyStats(),
  USD: createEmptyCurrencyStats(),
  Loans: { MXN: 0, USD: 0 },
  VAT: {
    issuerName: 'SMTLINK SOLUTIONS, S.A. de C.V.',
    MXN: createEmptyVatStats(),
    USD: createEmptyVatStats(),
    unassigned: {
      MXN: { grossCollected: 0, documentCount: 0 },
      USD: { grossCollected: 0, documentCount: 0 }
    }
  },
  filters: { date_from: null, date_to: null }
});

const getDefaultStatsDateRange = () => {
  return {
    dateFrom: '',
    dateTo: ''
  };
};

const OUTGOING_EMAIL_FROM = process.env.REACT_APP_OUTGOING_EMAIL_FROM || 'SMTLINKS <sales@smtlinkss.com>';
const DOCUMENT_EMAIL_TEMPLATES = [
  { value: 'document', label: 'Send Document' },
  { value: 'follow_up', label: 'Document Follow Up' },
  { value: 'payment', label: 'Payment Follow Up' },
  { value: 'custom', label: 'Custom Draft' }
];
const DOCUMENT_EMAIL_INSERTS = [
  { label: 'Greeting', snippet: 'I hope you are doing well.' },
  { label: 'Attached', snippet: 'Please find the requested document attached to this email.' },
  { label: 'Questions', snippet: 'Please let me know if you have any questions or require any changes.' },
  { label: 'Next Step', snippet: 'Please confirm the next step so we can continue.' },
  { label: 'Signature', snippet: 'Best regards,\n{sender_name}' }
];

const getDocumentEmailLabel = (type) => (
  type === 'quote' ? 'Quote' : type === 'purchase_order' ? 'Purchase Order' : 'Invoice'
);

const getDocumentNotesMeta = (notes) => {
  const raw = String(notes || '');
  if (!raw.startsWith('__PO_META__')) return {};
  const firstBreak = raw.indexOf('\n');
  const json = firstBreak === -1 ? raw.slice('__PO_META__'.length) : raw.slice('__PO_META__'.length, firstBreak);
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const buildDocumentEmailDraft = ({ template, invoice, senderName }) => {
  const documentLabel = getDocumentEmailLabel(invoice?.type);
  const documentNumber = invoice?.invoice_number || '';
  const notesMeta = getDocumentNotesMeta(invoice?.notes);
  const recipient = invoice?.type === 'purchase_order'
    ? (notesMeta.supplier_contact || notesMeta.supplier_name || 'there')
    : (invoice?.client_contact || invoice?.client_name || 'there');
  const subjectBase = `${documentLabel} ${documentNumber}`.trim();
  const signature = senderName || 'SMTLINKS Team';

  if (template === 'follow_up') {
    return {
      subject: `Follow up - ${subjectBase}`,
      message: `Hello ${recipient},\n\nI hope you are doing well.\n\nI am following up regarding ${subjectBase}. The document is attached for your reference.\n\nPlease let me know if you need any clarification or changes.\n\nBest regards,\n${signature}`
    };
  }
  if (template === 'payment') {
    return {
      subject: `Payment follow up - ${subjectBase}`,
      message: `Hello ${recipient},\n\nI hope you are doing well.\n\nI am following up on the payment status for ${subjectBase}. The document is attached for your reference.\n\nPlease let me know if you require any additional information.\n\nBest regards,\n${signature}`
    };
  }
  if (template === 'custom') {
    return {
      subject: subjectBase,
      message: `Hello ${recipient},\n\n\n\nBest regards,\n${signature}`
    };
  }
  return {
    subject: subjectBase,
    message: `Hello ${recipient},\n\nI hope you are doing well.\n\nPlease find ${subjectBase} attached to this email.\n\nPlease let me know if you have any questions or require any changes.\n\nBest regards,\n${signature}`
  };
};

const buildDocumentEmailFileName = (invoice) => {
  const prefix = invoice?.type === 'quote' ? 'QUOTE' : invoice?.type === 'purchase_order' ? 'PURCHASE_ORDER' : 'INVOICE';
  const number = String(invoice?.invoice_number || invoice?.id || 'DOCUMENT').replace(/[\\/:*?"<>|]+/g, '_');
  return `${prefix}_${number}.pdf`;
};

export default function FinanceManagement({ documentsOnly = false, permissionModule = 'finance', pageTitle = 'Finance' }) {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  // Detecta si es celular (pantalla menor a 'md' - 900px)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { hasPermission, canAccessBillingDocumentType } = usePermissions();
  const canCreateDocuments = hasPermission(permissionModule, 'create');
  const canEditDocuments = hasPermission(permissionModule, 'edit');
  const canDeleteDocuments = hasPermission(permissionModule, 'delete');
  const canApproveDocuments = hasPermission(permissionModule, 'approve');
  const canUpdateDocumentStatus = hasPermission(permissionModule, 'approve') || canEditDocuments;
  const canCreateFinanceRecords = hasPermission('finance', 'create');
  const canViewFinanceRecords = hasPermission('finance', 'view');
  const canDeleteFinanceRecords = hasPermission('finance', 'delete');
  const canViewInvoiceDocs = canAccessBillingDocumentType('invoice');
  const canViewQuoteDocs = canAccessBillingDocumentType('quote');
  const canViewPurchaseOrderDocs = canAccessBillingDocumentType('purchase_order');

  // --- Data States ---
  const [invoices, setInvoices] = useState([]);
  const [invoiceNumberSeedDocs, setInvoiceNumberSeedDocs] = useState({
    invoice: 'INV-001',
    quote: 'QT-001',
    purchase_order: 'PO-001'
  });
  const [expenses, setExpenses] = useState([]);
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [stats, setStats] = useState(createEmptyFinanceStats());
  const [projects, setProjects] = useState([]); 
  const [statusRules, setStatusRules] = useState(invoiceService.getFallbackStatusRules().rules);
  const [statusOptionsByType, setStatusOptionsByType] = useState(FALLBACK_STATUS_OPTIONS_BY_TYPE);
  const [statusLabelsByType, setStatusLabelsByType] = useState(FALLBACK_STATUS_LABELS_BY_TYPE);
  const [statusUpdatingById, setStatusUpdatingById] = useState({});
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [statusHistoryError, setStatusHistoryError] = useState('');
  const [statusHistoryInvoice, setStatusHistoryInvoice] = useState(null);
  const [statusHistoryRows, setStatusHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [notification, setNotification] = useState(null);

  // --- UI States ---
  const [currentTab, setCurrentTab] = useState(0); 
  
  // Filtros Avanzados
  const [showFilters, setShowFilters] = useState(false);
  const [showKpis, setShowKpis] = useState(!documentsOnly);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterExpensePaymentStatus, setFilterExpensePaymentStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statsDateRange, setStatsDateRange] = useState(getDefaultStatsDateRange);
  
  const [openDialog, setOpenDialog] = useState(false); 
  const [selectedItem, setSelectedItem] = useState(null); 
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expenseAttachmentsDialogOpen, setExpenseAttachmentsDialogOpen] = useState(false);
  const [selectedExpenseAttachments, setSelectedExpenseAttachments] = useState([]);
  const [selectedExpenseAttachmentTitle, setSelectedExpenseAttachmentTitle] = useState('');
  const [selectedExpenseAttachmentExpenseId, setSelectedExpenseAttachmentExpenseId] = useState(null);
  const [selectedExpenseAttachmentPreviewPath, setSelectedExpenseAttachmentPreviewPath] = useState('');
  const [selectedExpenseAttachmentPreviewUrl, setSelectedExpenseAttachmentPreviewUrl] = useState('');
  const [selectedExpenseAttachmentPreviewLoading, setSelectedExpenseAttachmentPreviewLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [documentPagination, setDocumentPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [rowActionLoadingById, setRowActionLoadingById] = useState({});
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const itemsPerPage = 10;
  const ancillaryLoadedRef = useRef(false);
  const ancillaryLoadingRef = useRef(null);
  const actionDedupRef = useRef({});
  const launchActionHandledRef = useRef('');
  const quoteReviewLaunchRef = useRef('');

  // Dialog States
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);

  const [fixedExpenseDialogOpen, setFixedExpenseDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentLedger, setPaymentLedger] = useState({ payments: [], summary: {} });
  const [paymentLedgerLoading, setPaymentLedgerLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState(createEmptyPaymentForm());
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTargetInvoice, setEmailTargetInvoice] = useState(null);
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('document');
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailAttachmentLoading, setEmailAttachmentLoading] = useState(false);
  const [emailAttachmentError, setEmailAttachmentError] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [commercialQuote, setCommercialQuote] = useState(null);
  const [financeBreakdown, setFinanceBreakdown] = useState(null);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [emailHistoryError, setEmailHistoryError] = useState('');
  const [quoteEmailDraft, setQuoteEmailDraft] = useState(null);
  const [quoteEmailWorkflowLoading, setQuoteEmailWorkflowLoading] = useState(false);
  const [quoteEmailWorkflowError, setQuoteEmailWorkflowError] = useState('');
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: ''
  });

  const currentUserDisplayName = useMemo(() => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return fullName || user?.username || user?.email || 'SMTLINKS Team';
  }, [user?.email, user?.first_name, user?.last_name, user?.username]);
  const replyToEmail = user?.email || 'No email configured in your profile';

  // --- Estados para Nuevo Gasto ---
  const [newExpense, setNewExpense] = useState({
    title: '', 
    category: '', 
    amount: '', 
    date: format(new Date(), 'yyyy-MM-dd'), 
    currency: 'MXN', 
    payment_status: 'paid',
    description: '',
    project_id: '' 
  });
  const [expenseFiles, setExpenseFiles] = useState([]);
  const [expenseUploadError, setExpenseUploadError] = useState('');
  const [expenseRemovedFiles, setExpenseRemovedFiles] = useState([]);

  const [newFixedExpense, setNewFixedExpense] = useState({
    title: '', category: '', amount: '', day_of_month: 1, description: ''
  });

  useEffect(() => {
    const requestedProjectId = String(searchParams.get('project_id') || '').trim();
    const requestedTab = String(searchParams.get('tab') || '').trim().toLowerCase();

    if (requestedProjectId) {
      setFilterProject(requestedProjectId);
      setShowFilters(true);
      setPage(1);
    }

    if (!requestedTab) return;

    if (requestedTab === 'quotes' && canViewQuoteDocs) {
      setCurrentTab(TAB_VALUES.quotes);
      return;
    }

    if (requestedTab === 'invoices' && canViewInvoiceDocs) {
      setCurrentTab(TAB_VALUES.invoices);
      return;
    }

    if (requestedTab === 'purchase_orders' && canViewPurchaseOrderDocs) {
      setCurrentTab(TAB_VALUES.purchaseOrders);
      return;
    }

    if (requestedTab === 'expenses') {
      setCurrentTab(TAB_VALUES.expenses);
    }
  }, [searchParams, canViewInvoiceDocs, canViewPurchaseOrderDocs, canViewQuoteDocs]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 260);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const runIdempotentAction = useCallback(async (key, action, cooldownMs = 1200) => {
    const now = Date.now();
    const lastAt = actionDedupRef.current[key] || 0;
    if (now - lastAt < cooldownMs) return null;
    actionDedupRef.current[key] = now;

    try {
      return await action();
    } finally {
      window.setTimeout(() => {
        if ((actionDedupRef.current[key] || 0) === now) {
          delete actionDedupRef.current[key];
        }
      }, cooldownMs);
    }
  }, []);

  const loadAncillaryData = useCallback(async () => {
    if (ancillaryLoadedRef.current) return;
    if (ancillaryLoadingRef.current) return ancillaryLoadingRef.current;

    ancillaryLoadingRef.current = Promise.all([
      projectService.getProjects({ lookup: 'billing_form', limit: 500 }),
      invoiceService.getStatusRules().catch(() => null),
      invoiceService.getNextDocumentNumbers().catch(() => ({ next_numbers: null }))
    ])
      .then(([projectsRes, statusRulesRes, seedInvoicesRes]) => {
        setProjects(projectsRes.projects || []);
        if (statusRulesRes?.rules) setStatusRules(statusRulesRes.rules);
        if (statusRulesRes?.optionsByType) setStatusOptionsByType(statusRulesRes.optionsByType);
        if (statusRulesRes?.labelsByType) setStatusLabelsByType(statusRulesRes.labelsByType);
        if (seedInvoicesRes?.next_numbers) setInvoiceNumberSeedDocs(seedInvoicesRes.next_numbers);
        ancillaryLoadedRef.current = true;
      })
      .finally(() => {
        ancillaryLoadingRef.current = null;
      });

    return ancillaryLoadingRef.current;
  }, []);

  useEffect(() => {
    loadAncillaryData().catch(() => null);
  }, [loadAncillaryData]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const isDocumentTab = ![TAB_VALUES.expenses, TAB_VALUES.fixedExpenses, TAB_VALUES.companyLoans].includes(currentTab);
      const requestedType = currentTab === TAB_VALUES.invoices
        ? 'invoice'
        : currentTab === TAB_VALUES.quotes
          ? 'quote'
          : currentTab === TAB_VALUES.purchaseOrders
            ? 'purchase_order'
            : null;
      const requestedStatus = isDocumentTab && filterStatus !== 'all' ? filterStatus : null;
      const requestedProjectId = isDocumentTab && filterProject !== 'all' ? filterProject : null;
      const requestedSearch = isDocumentTab ? debouncedSearchTerm : '';
      const statsPromise = !documentsOnly
        ? invoiceService.getInvoiceStats({
          date_from: statsDateRange.dateFrom,
          date_to: statsDateRange.dateTo
        }).catch(() => null)
        : Promise.resolve(null);
      const [invoicesRes, statsRes] = await Promise.all([
        invoiceService.getInvoices(requestedType, requestedStatus, {
          page,
          limit: itemsPerPage,
          q: requestedSearch || undefined,
          project_id: requestedProjectId || undefined
        }),
        statsPromise
      ]);

      setInvoices(invoicesRes.invoices || []);
      setDocumentPagination({
        page: Number(invoicesRes.page || page),
        limit: Number(invoicesRes.limit || itemsPerPage),
        total: Number(invoicesRes.total || 0),
        totalPages: Number(invoicesRes.total_pages || 1)
      });
      if (Number(invoicesRes.total_pages || 1) > 0 && page > Number(invoicesRes.total_pages || 1)) {
        setPage(Number(invoicesRes.total_pages || 1));
      }
      if (statsRes?.success) {
        const mxnStats = statsRes.by_currency?.MXN || createEmptyCurrencyStats();
        const usdStats = statsRes.by_currency?.USD || createEmptyCurrencyStats();
        const mxnVatStats = statsRes.vat_collected?.by_currency?.MXN || createEmptyVatStats();
        const usdVatStats = statsRes.vat_collected?.by_currency?.USD || createEmptyVatStats();
        setStats({
          MXN: { ...createEmptyCurrencyStats(), ...mxnStats },
          USD: { ...createEmptyCurrencyStats(), ...usdStats },
          Loans: {
            MXN: Number(mxnStats.loansAmount || 0),
            USD: Number(usdStats.loansAmount || 0)
          },
          VAT: {
            issuerName: statsRes.vat_collected?.issuer_name || 'SMTLINK SOLUTIONS, S.A. de C.V.',
            MXN: { ...createEmptyVatStats(), ...mxnVatStats },
            USD: { ...createEmptyVatStats(), ...usdVatStats },
            unassigned: {
              MXN: statsRes.vat_collected?.excluded_unassigned?.MXN || { grossCollected: 0, documentCount: 0 },
              USD: statsRes.vat_collected?.excluded_unassigned?.USD || { grossCollected: 0, documentCount: 0 }
            }
          },
          filters: {
            date_from: statsRes.filters?.date_from || statsDateRange.dateFrom,
            date_to: statsRes.filters?.date_to || statsDateRange.dateTo
          }
        });
      } else if (!documentsOnly) {
        setStats((prev) => ({
          ...createEmptyFinanceStats(),
          filters: {
            date_from: prev.filters?.date_from || statsDateRange.dateFrom,
            date_to: prev.filters?.date_to || statsDateRange.dateTo
          }
        }));
      } else {
        setStats(createEmptyFinanceStats());
      }

      if (!documentsOnly) {
        try {
          const [expensesRes, fixedRes] = await Promise.all([
            invoiceService.getExpenses({
              payment_status: filterExpensePaymentStatus !== 'all' ? filterExpensePaymentStatus : undefined
            }),
            invoiceService.getFixedExpenses().catch(() => ({ success: true, fixed_expenses: [] }))
          ]);
          setExpenses(expensesRes.success ? expensesRes.expenses : []);
          setFixedExpenses(fixedRes.success ? (fixedRes.fixed_expenses || []) : []);
        } catch (e) {
          console.warn('No financial data loaded yet');
        }
      } else {
        setExpenses([]);
        setFixedExpenses([]);
      }

    } catch (err) {
      setError('Error loading data: ' + err.message);
    } finally {
      setHasLoadedOnce(true);
      setLoading(false);
    }
  }, [documentsOnly, statsDateRange.dateFrom, statsDateRange.dateTo, currentTab, filterStatus, filterProject, debouncedSearchTerm, page, filterExpensePaymentStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!documentsOnly) return;
    if ([TAB_VALUES.expenses, TAB_VALUES.fixedExpenses, TAB_VALUES.companyLoans].includes(currentTab)) {
      setCurrentTab(TAB_VALUES.allDocs);
    }
  }, [documentsOnly, currentTab]);

  useEffect(() => {
    if (currentTab === TAB_VALUES.invoices && !canViewInvoiceDocs) setCurrentTab(TAB_VALUES.allDocs);
    if (currentTab === TAB_VALUES.quotes && !canViewQuoteDocs) setCurrentTab(TAB_VALUES.allDocs);
    if (currentTab === TAB_VALUES.purchaseOrders && !canViewPurchaseOrderDocs) setCurrentTab(TAB_VALUES.allDocs);
  }, [currentTab, canViewInvoiceDocs, canViewQuoteDocs, canViewPurchaseOrderDocs]);

  useEffect(() => {
    setPage(1);
  }, [currentTab, filterStatus, filterProject, filterCategory, filterExpensePaymentStatus, searchTerm]);

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ message, severity });
    if(severity === 'error') setError(message);
  }, []);

  const loadPaymentLedger = useCallback(async (invoice) => {
    if (!invoice?.id) return;
    try {
      setPaymentLedgerLoading(true);
      const response = await invoiceService.getDocumentPayments(invoice.id);
      setPaymentLedger({ payments: response.payments || [], summary: response.summary || {} });
    } catch (requestError) {
      showNotification(requestError.response?.data?.message || 'Unable to load payment installments.', 'error');
    } finally {
      setPaymentLedgerLoading(false);
    }
  }, [showNotification]);

  const handleOpenPaymentLedger = useCallback(async (invoice) => {
    setPaymentTarget(invoice);
    setPaymentForm(createEmptyPaymentForm(invoice.type));
    setPaymentLedger({ payments: [], summary: {} });
    setPaymentDialogOpen(true);
    await loadPaymentLedger(invoice);
  }, [loadPaymentLedger]);

  const handleSaveDocumentPayment = useCallback(async () => {
    if (!paymentTarget?.id || Number(paymentForm.amount) <= 0) return;
    try {
      setPaymentSaving(true);
      const response = await invoiceService.createDocumentPayment(paymentTarget.id, paymentForm);
      setPaymentLedger({ payments: response.payments || [], summary: response.summary || {} });
      setPaymentForm(createEmptyPaymentForm(paymentTarget.type));
      showNotification('Payment installment registered.');
      await loadData();
    } catch (requestError) {
      showNotification(requestError.response?.data?.message || requestError.message, 'error');
    } finally {
      setPaymentSaving(false);
    }
  }, [loadData, paymentForm, paymentTarget, showNotification]);

  const handleDeleteDocumentPayment = useCallback(async (payment) => {
    if (!paymentTarget?.id || !window.confirm(`Delete the payment of ${Number(payment.amount || 0).toFixed(2)} ${payment.currency || 'MXN'}?`)) return;
    try {
      setPaymentSaving(true);
      const response = await invoiceService.deleteDocumentPayment(paymentTarget.id, payment.id);
      setPaymentLedger({ payments: response.payments || [], summary: response.summary || {} });
      showNotification('Payment installment deleted.');
      await loadData();
    } catch (requestError) {
      showNotification(requestError.response?.data?.message || requestError.message, 'error');
    } finally {
      setPaymentSaving(false);
    }
  }, [loadData, paymentTarget, showNotification]);

  const openBlobInNewTab = useCallback((blob, mimeType = 'application/octet-stream', fallbackName = 'file') => (
    openBlobFile(blob, { mimeType, fileName: fallbackName })
  ), []);

  const formatCurrency = useCallback((amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(amount || 0);
  }, []);

  const withRowActionLoading = useCallback(async (invoiceId, task) => {
    setRowActionLoadingById((prev) => ({ ...prev, [invoiceId]: true }));
    try {
      return await task();
    } finally {
      setRowActionLoadingById((prev) => {
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      });
    }
  }, []);

  const triggerBlobDownload = useCallback((blob, fileName, mimeType) => (
    downloadBlobFile(blob, { mimeType, fileName })
  ), []);

  const getExportFiltersForCurrentView = useCallback(() => {
    const baseProjectId = filterProject !== 'all' ? filterProject : undefined;
    const baseSearch = debouncedSearchTerm || undefined;

    if (currentTab === TAB_VALUES.expenses) {
      return {
        dataset: 'expenses',
        project_id: baseProjectId,
        category: filterCategory !== 'all' ? filterCategory : undefined,
        q: baseSearch
      };
    }

    if (currentTab === TAB_VALUES.fixedExpenses) {
      return { dataset: 'fixed_expenses' };
    }

    if (currentTab === TAB_VALUES.companyLoans) {
      return {
        dataset: 'company_loans',
        project_id: baseProjectId,
        q: baseSearch
      };
    }

    return {
      dataset: 'documents',
      type: currentTab === TAB_VALUES.invoices
        ? 'invoice'
        : currentTab === TAB_VALUES.quotes
          ? 'quote'
          : currentTab === TAB_VALUES.purchaseOrders
            ? 'purchase_order'
            : undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      project_id: baseProjectId,
      q: baseSearch
    };
  }, [currentTab, filterCategory, filterProject, filterStatus, debouncedSearchTerm]);

  const projectsById = useMemo(() => (
    projects.reduce((acc, project) => {
      acc[String(project.id)] = project;
      return acc;
    }, {})
  ), [projects]);

  // --- Handlers ---
  const handleOpenCreateInvoice = useCallback(() => {
    loadAncillaryData().catch(() => null);
    setInvoiceToEdit(null);
    setInvoiceDialogOpen(true);
  }, [loadAncillaryData]);
  const handleOpenCreateExpense = useCallback(() => {
    loadAncillaryData().catch(() => null);
    setExpenseToEdit(null);
    setNewExpense({ title: '', category: '', amount: '', date: format(new Date(), 'yyyy-MM-dd'), currency: 'MXN', payment_status: 'paid', description: '', project_id: '' });
    setExpenseFiles([]);
    setExpenseUploadError('');
    setExpenseRemovedFiles([]);
    setExpenseDialogOpen(true);
  }, [loadAncillaryData]);

  useEffect(() => {
    const requestedAction = String(searchParams.get('action') || '').trim().toLowerCase();
    if (!requestedAction || launchActionHandledRef.current === requestedAction) return;

    launchActionHandledRef.current = requestedAction;
    if (requestedAction === 'new-document' && canCreateDocuments) {
      setCurrentTab(TAB_VALUES.invoices);
      handleOpenCreateInvoice();
      return;
    }
    if (requestedAction === 'new-expense' && canCreateFinanceRecords && !documentsOnly) {
      setCurrentTab(TAB_VALUES.expenses);
      handleOpenCreateExpense();
      return;
    }
    if (requestedAction === 'payment' && canCreateFinanceRecords && !documentsOnly) {
      setCurrentTab(TAB_VALUES.invoices);
      setNotification({
        severity: 'info',
        message: 'Select the payment icon on an invoice to record a payment.'
      });
    }
  }, [
    canCreateDocuments,
    canCreateFinanceRecords,
    documentsOnly,
    handleOpenCreateExpense,
    handleOpenCreateInvoice,
    searchParams
  ]);
  const handleOpenEditExpense = useCallback((expense) => { setExpenseToEdit(expense); setNewExpense({ title: expense.title, category: expense.category, amount: expense.amount, date: expense.date ? expense.date.split('T')[0] : format(new Date(), 'yyyy-MM-dd'), currency: expense.currency || 'MXN', payment_status: expense.payment_status || 'paid', description: expense.description || '', project_id: expense.project_id || '' }); setExpenseFiles([]); setExpenseUploadError(''); setExpenseRemovedFiles([]); setExpenseDialogOpen(true); }, []);
  const handleOpenCreateFixedExpense = useCallback(() => { setNewFixedExpense({ title: '', category: '', amount: '', day_of_month: 1, description: '' }); setFixedExpenseDialogOpen(true); }, []);
  const handleOpenEditInvoice = useCallback((invoice) => {
    loadAncillaryData().catch(() => null);
    setInvoiceToEdit(invoice);
    setInvoiceDialogOpen(true);
  }, [loadAncillaryData]);
  const loadAutomaticEmailAttachment = useCallback(async (invoice) => {
    if (!invoice?.id) return null;

    if (invoice.type === 'invoice' && invoice.file_path) {
      const officialFile = await invoiceService.getInvoiceOfficialFile(invoice.id);
      return {
        file: new File([officialFile.blob], officialFile.fileName || `INVOICE_${invoice.invoice_number || invoice.id}`, {
          type: officialFile.contentType || officialFile.blob?.type || 'application/octet-stream'
        }),
        source: 'system'
      };
    }

    const pdfBlob = await invoiceService.downloadInvoicePDF(invoice.id);
    return {
      file: new File([pdfBlob], buildDocumentEmailFileName(invoice), { type: 'application/pdf' }),
      source: 'system'
    };
  }, []);

  const loadInvoiceEmailHistory = useCallback(async (invoiceId) => {
    if (!invoiceId) return;
    setEmailHistoryLoading(true);
    setEmailHistoryError('');
    try {
      const response = await invoiceService.getEmailHistory(invoiceId);
      setEmailHistory(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setEmailHistory([]);
      setEmailHistoryError(
        error?.response?.data?.message || 'The sent email history could not be loaded.'
      );
    } finally {
      setEmailHistoryLoading(false);
    }
  }, []);

  const handleOpenSendInvoiceDialog = useCallback(async (invoice) => {
    let resolvedInvoice = invoice;
    try {
      const response = await invoiceService.getInvoiceById(invoice.id);
      if (response?.invoice) resolvedInvoice = response.invoice;
    } catch (error) {
      resolvedInvoice = invoice;
    }

    const nextTemplate = 'document';
    const draft = buildDocumentEmailDraft({
      template: nextTemplate,
      invoice: resolvedInvoice,
      senderName: currentUserDisplayName
    });
    const notesMeta = getDocumentNotesMeta(resolvedInvoice?.notes);
    const defaultRecipient = resolvedInvoice?.type === 'purchase_order'
      ? (notesMeta.supplier_email || '')
      : (resolvedInvoice?.contact_email || resolvedInvoice?.client_email || '');
    setEmailTargetInvoice(resolvedInvoice);
    setEmailTemplate(nextTemplate);
    setEmailForm({ to: defaultRecipient, cc: '', subject: draft.subject, message: draft.message });
    setEmailAttachments([]);
    setEmailAttachmentError('');
    setEmailHistory([]);
    setEmailHistoryError('');
    setQuoteEmailDraft(null);
    setQuoteEmailWorkflowError('');
    setEmailDialogOpen(true);
    setEmailAttachmentLoading(true);
    loadInvoiceEmailHistory(resolvedInvoice.id);

    if (resolvedInvoice.type === 'quote') {
      setQuoteEmailWorkflowLoading(true);
      try {
        let draftResponse = invoice.requestedDraftId
          ? await invoiceService.getQuoteEmailDraft(resolvedInvoice.id, invoice.requestedDraftId)
          : await invoiceService.getLatestQuoteEmailDraft(resolvedInvoice.id);
        let workflowDraft = draftResponse?.draft || null;
        if (!workflowDraft || ['sent', 'cancelled', 'error'].includes(workflowDraft.status)) {
          if (invoice.requestedDraftId) throw new Error('El borrador ya no está disponible para revisión. Actualiza el seguimiento antes de continuar.');
          draftResponse = await invoiceService.generateQuoteEmailDraft(resolvedInvoice.id);
          workflowDraft = draftResponse?.draft || null;
        }
        if (!workflowDraft) throw new Error('The quote email draft could not be prepared.');
        setQuoteEmailDraft(workflowDraft);
        setEmailForm({
          to: (workflowDraft.to || []).join('; '),
          cc: (workflowDraft.cc || []).join('; '),
          subject: workflowDraft.subject || '',
          message: workflowDraft.body_text || ''
        });
      } catch (error) {
        setQuoteEmailWorkflowError(error?.response?.data?.message || error.message || 'The AI draft could not be loaded.');
      } finally {
        setQuoteEmailWorkflowLoading(false);
      }
    }

    try {
      const automaticAttachment = await loadAutomaticEmailAttachment(resolvedInvoice);
      if (automaticAttachment) setEmailAttachments([automaticAttachment]);
    } catch (error) {
      setEmailAttachmentError('The system document could not be prepared automatically. You can still add it manually.');
    } finally {
      setEmailAttachmentLoading(false);
    }
  }, [currentUserDisplayName, loadAutomaticEmailAttachment, loadInvoiceEmailHistory]);

  const handleCloseInvoiceEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    setEmailTargetInvoice(null);
    setEmailTemplate('document');
    setEmailAttachments([]);
    setEmailAttachmentError('');
    setEmailAttachmentLoading(false);
    setEmailHistory([]);
    setEmailHistoryError('');
    setEmailHistoryLoading(false);
    setQuoteEmailDraft(null);
    setQuoteEmailWorkflowError('');
    setQuoteEmailWorkflowLoading(false);
  }, []);

  useEffect(() => {
    const action = String(searchParams.get('action') || '').trim().toLowerCase();
    const invoiceId = Number(searchParams.get('invoice_id'));
    const draftId = Number(searchParams.get('draft_id'));
    const launchKey = `${action}:${invoiceId || ''}`;
    if (action !== 'review-quote-email' || !invoiceId || quoteReviewLaunchRef.current === launchKey) return;
    quoteReviewLaunchRef.current = launchKey;
    setCurrentTab(TAB_VALUES.quotes);
    handleOpenSendInvoiceDialog({ id: invoiceId, type: 'quote', requestedDraftId: draftId || null });
  }, [handleOpenSendInvoiceDialog, searchParams]);

  const buildQuoteDraftPayload = useCallback((draft = quoteEmailDraft) => ({
    to: emailForm.to,
    cc: emailForm.cc,
    subject: emailForm.subject,
    body: emailForm.message,
    follow_up_days: draft?.follow_up_days || 7,
    version: draft?.version
  }), [emailForm, quoteEmailDraft]);

  const handleSubmitQuoteEmailDraft = useCallback(async () => {
    if (!emailTargetInvoice?.id || !quoteEmailDraft?.id) return;
    setQuoteEmailWorkflowLoading(true);
    setQuoteEmailWorkflowError('');
    try {
      const saved = await invoiceService.updateQuoteEmailDraft(
        emailTargetInvoice.id,
        quoteEmailDraft.id,
        buildQuoteDraftPayload()
      );
      const submitted = await invoiceService.submitQuoteEmailDraft(emailTargetInvoice.id, quoteEmailDraft.id);
      setQuoteEmailDraft(submitted?.draft || saved?.draft);
      showNotification('✅ Draft sent for human approval.');
    } catch (error) {
      setQuoteEmailWorkflowError(error?.response?.data?.message || error.message || 'The draft could not be submitted.');
    } finally {
      setQuoteEmailWorkflowLoading(false);
    }
  }, [buildQuoteDraftPayload, emailTargetInvoice, quoteEmailDraft, showNotification]);

  const handleApproveQuoteEmailDraft = useCallback(async () => {
    if (!emailTargetInvoice?.id || !quoteEmailDraft?.id) return;
    setQuoteEmailWorkflowLoading(true);
    setQuoteEmailWorkflowError('');
    try {
      const response = await invoiceService.approveQuoteEmailDraft(
        emailTargetInvoice.id,
        quoteEmailDraft.id,
        buildQuoteDraftPayload()
      );
      setQuoteEmailDraft(response?.draft || null);
      showNotification('✅ Quote email approved. It is ready to send.');
    } catch (error) {
      setQuoteEmailWorkflowError(error?.response?.data?.message || error.message || 'The draft could not be approved.');
    } finally {
      setQuoteEmailWorkflowLoading(false);
    }
  }, [buildQuoteDraftPayload, emailTargetInvoice, quoteEmailDraft, showNotification]);

  const handleRejectQuoteEmailDraft = useCallback(async () => {
    if (!emailTargetInvoice?.id || !quoteEmailDraft?.id) return;
    const reason = window.prompt('Describe the changes required before this email can be sent:');
    if (!reason?.trim()) return;
    setQuoteEmailWorkflowLoading(true);
    setQuoteEmailWorkflowError('');
    try {
      const response = await invoiceService.rejectQuoteEmailDraft(emailTargetInvoice.id, quoteEmailDraft.id, reason.trim());
      setQuoteEmailDraft(response?.draft || null);
      showNotification('Draft returned for changes.', 'warning');
    } catch (error) {
      setQuoteEmailWorkflowError(error?.response?.data?.message || error.message || 'The draft could not be rejected.');
    } finally {
      setQuoteEmailWorkflowLoading(false);
    }
  }, [emailTargetInvoice, quoteEmailDraft, showNotification]);

  const handleApplyInvoiceEmailTemplate = useCallback((template) => {
    if (!emailTargetInvoice) return;
    const draft = buildDocumentEmailDraft({ template, invoice: emailTargetInvoice, senderName: currentUserDisplayName });
    setEmailTemplate(template);
    setEmailForm((prev) => ({ ...prev, subject: draft.subject, message: draft.message }));
  }, [currentUserDisplayName, emailTargetInvoice]);

  const handleInsertInvoiceEmailSnippet = useCallback((snippet) => {
    const resolved = String(snippet || '').replaceAll('{sender_name}', currentUserDisplayName);
    setEmailForm((prev) => ({
      ...prev,
      message: `${prev.message || ''}${prev.message && !prev.message.endsWith('\n') ? '\n' : ''}${resolved}`
    }));
  }, [currentUserDisplayName]);

  const handleInvoiceEmailFilesSelected = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setEmailAttachments((prev) => {
      const existing = new Set(prev.map(({ file }) => `${file.name}:${file.size}:${file.lastModified}`));
      const next = [...prev];
      files.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!existing.has(key)) {
          existing.add(key);
          next.push({ file, source: 'manual' });
        }
      });
      return next;
    });
    event.target.value = '';
  }, []);

  const handleRemoveInvoiceEmailAttachment = useCallback((indexToRemove) => {
    setEmailAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleDuplicateInvoice = useCallback(async (invoice) => {
    if (!invoice?.id) return;
    try {
      const response = await withRowActionLoading(invoice.id, () => invoiceService.duplicateInvoice(invoice.id));
      if (!response?.success) throw new Error(response?.message || 'The document could not be duplicated');
      showNotification(`✅ Document duplicated as ${response?.invoice?.invoice_number || 'new record'}`);
      await loadData();
      if (response?.invoice) {
        setInvoiceToEdit(response.invoice);
        setInvoiceDialogOpen(true);
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      showNotification(`Error duplicating document: ${backendMessage || err.message}`, 'error');
    }
  }, [loadData, showNotification, withRowActionLoading]);

  const handleConvertQuoteToInvoice = useCallback(async (invoice) => {
    if (!invoice?.id) return;
    try {
      const response = await withRowActionLoading(invoice.id, () => invoiceService.convertQuoteToInvoice(invoice.id));
      if (!response?.success) throw new Error(response?.message || 'The quotation could not be converted');
      showNotification(`✅ Quotation converted to ${response?.invoice?.invoice_number || 'invoice'}`);
      await loadData();
      if (response?.invoice) {
        setInvoiceToEdit(response.invoice);
        setInvoiceDialogOpen(true);
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      showNotification(`Error converting quotation: ${backendMessage || err.message}`, 'error');
    }
  }, [loadData, showNotification, withRowActionLoading]);

  const handleSendInvoiceEmail = useCallback(async () => {
    if (!emailTargetInvoice?.id) return;
    try {
      setSendingInvoiceEmail(true);
      const response = await invoiceService.sendInvoiceByEmail(emailTargetInvoice.id, {
        ...emailForm,
        draft_id: emailTargetInvoice.type === 'quote' ? quoteEmailDraft?.id : undefined,
        attachments: emailAttachments.map(({ file }) => file)
      });
      if (!response?.success) throw new Error(response?.message || 'The email could not be sent');
      showNotification(
        response.followup_scheduled === false && emailTargetInvoice.type === 'quote' && quoteEmailDraft?.generation_source !== 'followup'
          ? 'Document sent, but the automatic follow-up could not be scheduled. Contact an administrator.'
          : `✅ Document sent to ${response.sent_to?.join(', ') || 'recipient'}`,
        response.followup_scheduled === false && emailTargetInvoice.type === 'quote' && quoteEmailDraft?.generation_source !== 'followup' ? 'warning' : 'success'
      );
      handleCloseInvoiceEmailDialog();
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      showNotification(`Error sending email: ${backendMessage || err.message}`, 'error');
    } finally {
      setSendingInvoiceEmail(false);
    }
  }, [emailAttachments, emailForm, emailTargetInvoice, handleCloseInvoiceEmailDialog, quoteEmailDraft, showNotification]);

  const handleExportDataset = useCallback(async (formatType) => {
    try {
      const filters = getExportFiltersForCurrentView();
      const isExcel = formatType === 'excel';
      const blob = isExcel
        ? await invoiceService.exportFinanceExcel(filters)
        : await invoiceService.exportFinanceCsv(filters);
      const fileBase = filters.dataset || 'documents';
      const extension = isExcel ? 'xlsx' : 'csv';
      const mimeType = isExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      await triggerBlobDownload(blob, `${fileBase}_${format(new Date(), 'yyyyMMdd_HHmm')}.${extension}`, mimeType);
      showNotification(`✅ Export ${isExcel ? 'Excel' : 'CSV'} generado correctamente`);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      showNotification(`Error exporting data: ${backendMessage || err.message}`, 'error');
    }
  }, [getExportFiltersForCurrentView, showNotification, triggerBlobDownload]);

  const handleRemoveExistingExpenseFile = useCallback((storedPath) => {
    if (!storedPath) return;
    setExpenseRemovedFiles((prev) => (prev.includes(storedPath) ? prev : [...prev, storedPath]));
  }, []);

  // --- SAVE / DELETE / PROCESS HANDLERS ---
  const handleSaveInvoice = async (data, file, descriptionImages = [], intercompanyInvoiceFile = null) => {
    try {
      if (!canCreateDocuments && !canEditDocuments) {
        showNotification('No tienes permiso para crear o editar documentos', 'error');
        return;
      }
      const formData = new FormData();
      Object.keys(data).forEach(key => { if (data[key] !== null && data[key] !== undefined) formData.append(key, data[key]); });
      if (file) formData.append('file', file);
      descriptionImages.forEach((image) => formData.append('description_images', image));
      if (intercompanyInvoiceFile) formData.append('intercompany_invoice', intercompanyInvoiceFile);
      if (invoiceToEdit) await invoiceService.updateInvoice(data.id, formData);
      else await invoiceService.createInvoice(formData);
      showNotification('✅ Document saved successfully'); setInvoiceDialogOpen(false); setInvoiceToEdit(null); await loadData();
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      const validationErrors = err?.response?.data?.errors;
      const validationDetail = Array.isArray(validationErrors) && validationErrors.length
        ? validationErrors.slice(0, 3).map(({ field, message }) => `${field}: ${message}`).join('; ')
        : '';
      showNotification(`Error saving document: ${validationDetail || backendMessage || err.message}`, 'error');
    }
  };

  const handleSaveExpense = async () => {
    try {
        const uploadError = expenseUploadError || expenseReceiptCountError(expenseFiles);
        if (uploadError) { setExpenseUploadError(uploadError); return; }
        if(!newExpense.title || !newExpense.amount) return;
        const formData = new FormData();
        formData.append('title', newExpense.title); formData.append('category', newExpense.category); formData.append('amount', newExpense.amount); formData.append('date', newExpense.date); formData.append('currency', newExpense.currency); formData.append('payment_status', newExpense.payment_status || 'paid'); formData.append('description', newExpense.description || '');
        if (newExpense.project_id) formData.append('project_id', newExpense.project_id);
        expenseFiles.forEach((file) => formData.append('receipts', file));
        if (expenseToEdit && expenseRemovedFiles.length > 0) {
          formData.append('remove_receipt_paths', JSON.stringify(expenseRemovedFiles));
        }
        
        let response;
        if (expenseToEdit) response = await invoiceService.updateExpense(expenseToEdit.id, formData); 
        else response = await invoiceService.createExpense(formData);
        
        if (response.success) { showNotification('✅ Expense saved successfully'); setExpenseDialogOpen(false); setExpenseToEdit(null); setExpenseRemovedFiles([]); await loadData(); }
    } catch (err) { showNotification('Error saving expense: ' + (err?.response?.data?.message || err.message), 'error'); }
  };

  const handleSaveFixedExpense = async () => {
    try {
        if(!newFixedExpense.title || !newFixedExpense.amount) return;
        const response = await invoiceService.createFixedExpense(newFixedExpense);
        if (response.success) { showNotification('✅ Fixed Expense configured'); setFixedExpenseDialogOpen(false); await loadData(); }
    } catch (err) { showNotification('Error saving fixed expense: ' + err.message, 'error'); }
  };

  const handleProcessFixedExpense = async (id) => {
    try { setLoading(true); const response = await invoiceService.processFixedExpense(id); if (response.success) { showNotification('✅ Expense recorded for this month!'); await loadData(); } } catch (err) { showNotification('Error processing expense: ' + err.message, 'error'); } finally { setLoading(false); }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
        if (deleteConfirm.type === 'expense') await invoiceService.deleteExpense(deleteConfirm.id);
        else if (deleteConfirm.type === 'fixed_expense') await invoiceService.deleteFixedExpense(deleteConfirm.id);
        else await invoiceService.deleteInvoice(deleteConfirm.id);
        showNotification('✅ Item deleted'); setDeleteConfirm(null); await loadData();
    } catch (err) {
        const backendMessage = err?.response?.data?.message;
        showNotification(`Error deleting item: ${backendMessage || err.message}`, 'error');
    }
  };

  const handleOpenExpenseAttachments = useCallback((expense) => {
    const files = Array.isArray(expense?.receipt_files)
      ? expense.receipt_files
      : (expense?.receipt_path ? [expense.receipt_path] : []);
    setSelectedExpenseAttachments(files);
    setSelectedExpenseAttachmentExpenseId(expense?.id || null);
    setSelectedExpenseAttachmentPreviewPath(files[0] || '');
    setSelectedExpenseAttachmentTitle(expense?.title || 'Expense');
    setExpenseAttachmentsDialogOpen(true);
  }, []);

  const getAttachmentExtension = useCallback((storedPath) => {
    const cleanPath = String(storedPath || '').split('?')[0].toLowerCase();
    const parts = cleanPath.split('.');
    return parts.length > 1 ? parts.pop() : '';
  }, []);

  const isImageAttachment = useCallback((storedPath) => {
    const ext = getAttachmentExtension(storedPath);
    return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  }, [getAttachmentExtension]);

  const isPdfAttachment = useCallback((storedPath) => {
    const ext = getAttachmentExtension(storedPath);
    return ext === 'pdf';
  }, [getAttachmentExtension]);

  const isPreviewableStoredFile = useCallback((storedPath) => {
    const ext = getAttachmentExtension(storedPath);
    return ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'txt', 'xml'].includes(ext);
  }, [getAttachmentExtension]);

  useEffect(() => {
    if (!expenseAttachmentsDialogOpen || !selectedExpenseAttachmentPreviewPath || !selectedExpenseAttachmentExpenseId) {
      setSelectedExpenseAttachmentPreviewLoading(false);
      setSelectedExpenseAttachmentPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }

    if (!isPreviewableStoredFile(selectedExpenseAttachmentPreviewPath)) {
      setSelectedExpenseAttachmentPreviewLoading(false);
      setSelectedExpenseAttachmentPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }

    const selectedIndex = selectedExpenseAttachments.findIndex((storedPath) => storedPath === selectedExpenseAttachmentPreviewPath);
    if (selectedIndex < 0) return undefined;

    let active = true;
    setSelectedExpenseAttachmentPreviewLoading(true);

    invoiceService.getExpenseReceiptFile(selectedExpenseAttachmentExpenseId, selectedIndex)
      .then((fileResponse) => {
        if (!active) return;
        setSelectedExpenseAttachmentPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return window.URL.createObjectURL(new Blob([fileResponse.blob], {
            type: fileResponse.contentType || 'application/octet-stream'
          }));
        });
      })
      .catch(() => {
        if (!active) return;
        setSelectedExpenseAttachmentPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return '';
        });
      })
      .finally(() => {
        if (active) setSelectedExpenseAttachmentPreviewLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    expenseAttachmentsDialogOpen,
    selectedExpenseAttachmentExpenseId,
    selectedExpenseAttachmentPreviewPath,
    selectedExpenseAttachments,
    isPreviewableStoredFile
  ]);

  const handleDownloadPDF = async (doc) => {
    try {
      if (!doc?.id) throw new Error('Invalid document');

      const blob = await invoiceService.downloadInvoicePDF(doc.id);
      const prefix = doc.type === 'purchase_order' ? 'PURCHASE_ORDER' : doc.type === 'quote' ? 'QUOTE' : 'INVOICE';
      await downloadBlobFile(blob, {
        fileName: `${prefix}_${doc.invoice_number || doc.id}.pdf`,
        mimeType: 'application/pdf'
      });

      showNotification('📄 PDF generated from backend successfully');
    } catch (err) {
      // Fallback local si el endpoint no está desplegado aún en el servidor.
      generatePurchaseOrderPDF(doc);
      showNotification('⚠️ Backend PDF unavailable, generated local copy', 'error');
    }
  };

  const handleViewInvoice = useCallback(async (invoice) => {
    try {
      if (!invoice?.id) throw new Error('Invalid document');

      const blob = await invoiceService.downloadInvoicePDF(invoice.id);
      await openBlobInNewTab(blob, 'application/pdf', `${invoice.invoice_number || invoice.id}.pdf`);
    } catch (err) {
      if (invoice?.file_path) {
        const fileResponse = await invoiceService.getInvoiceOfficialFile(invoice.id);
        await openBlobInNewTab(fileResponse.blob, fileResponse.contentType, fileResponse.fileName);
        showNotification('The official PDF could not be opened; the attached file was displayed instead.', 'error');
        return;
      }
      setSelectedItem(invoice);
      setOpenDialog(true);
      showNotification('The file could not be opened; displaying the document summary instead.', 'error');
    }
  }, [openBlobInNewTab, showNotification]);

  const handleOpenUploadedInvoiceFile = useCallback(async (invoice) => {
    try {
      if (!invoice?.id) throw new Error('Invalid document');
      const previewable = isPreviewableStoredFile(invoice?.file_path || '');
      const fileResponse = await invoiceService.getInvoiceOfficialFile(invoice.id, { download: !previewable });
      if (previewable) {
        await openBlobInNewTab(fileResponse.blob, fileResponse.contentType, fileResponse.fileName);
      } else {
        await triggerBlobDownload(fileResponse.blob, fileResponse.fileName, fileResponse.contentType);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || 'The attachment could not be opened', 'error');
    }
  }, [isPreviewableStoredFile, openBlobInNewTab, showNotification, triggerBlobDownload]);

  const handleOpenExpenseAttachment = useCallback(async (expenseId, fileIndex, storedPath = '') => {
    try {
      const previewable = isPreviewableStoredFile(storedPath);
      const fileResponse = await invoiceService.getExpenseReceiptFile(expenseId, fileIndex, { download: !previewable });
      if (previewable) {
        await openBlobInNewTab(fileResponse.blob, fileResponse.contentType, fileResponse.fileName);
      } else {
        await triggerBlobDownload(fileResponse.blob, fileResponse.fileName, fileResponse.contentType);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || 'The expense attachment could not be opened', 'error');
    }
  }, [isPreviewableStoredFile, openBlobInNewTab, showNotification, triggerBlobDownload]);

  const handleViewInvoiceSafe = useCallback((invoice) => (
    runIdempotentAction(
      `view-invoice:${invoice?.id}`,
      () => withRowActionLoading(invoice?.id, () => handleViewInvoice(invoice))
    )
  ), [handleViewInvoice, runIdempotentAction, withRowActionLoading]);

  const handleOpenUploadedInvoiceFileSafe = useCallback((invoice) => (
    runIdempotentAction(
      `view-uploaded-invoice:${invoice?.id}`,
      () => withRowActionLoading(invoice?.id, () => handleOpenUploadedInvoiceFile(invoice))
    )
  ), [handleOpenUploadedInvoiceFile, runIdempotentAction, withRowActionLoading]);

  const handleOpenExpenseAttachmentSafe = useCallback((expenseId, fileIndex, storedPath = '') => (
    runIdempotentAction(`expense-attachment:${expenseId}:${fileIndex}`, () => handleOpenExpenseAttachment(expenseId, fileIndex, storedPath))
  ), [handleOpenExpenseAttachment, runIdempotentAction]);

  const handleQuickStatusChange = async (invoice, nextStatusRaw) => {
    if (!canUpdateDocumentStatus) {
      showNotification('No tienes permiso para actualizar el status', 'error');
      return;
    }
    const nextStatus = (nextStatusRaw || '').toString().toLowerCase().trim();
    const currentStatus = (invoice?.status || '').toString().toLowerCase().trim();
    if (!invoice?.id || !nextStatus || nextStatus === currentStatus) return;

    try {
      setStatusUpdatingById((prev) => ({ ...prev, [invoice.id]: true }));
      const payload = new FormData();
      payload.append('status', nextStatus);

      const response = await invoiceService.updateInvoice(invoice.id, payload);
      if (!response?.success) {
        throw new Error(response?.message || 'The status could not be updated');
      }

      setInvoices((prev) => prev.map((row) => (
        row.id === invoice.id ? { ...row, status: nextStatus } : row
      )));
      const nextLabel = getStatusConfigByType(invoice.type, nextStatus, statusLabelsByType).label;
      showNotification(`✅ Status actualizado a "${nextLabel}"`);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      showNotification(`Error updating status: ${backendMessage || err.message}`, 'error');
    } finally {
      setStatusUpdatingById((prev) => {
        const clone = { ...prev };
        delete clone[invoice.id];
        return clone;
      });
    }
  };

  const handleOpenStatusHistory = async (invoice) => {
    if (!invoice?.id) return;

    setStatusHistoryOpen(true);
    setStatusHistoryLoading(true);
    setStatusHistoryError('');
    setStatusHistoryInvoice(invoice);
    setStatusHistoryRows([]);

    try {
      const response = await invoiceService.getInvoiceStatusHistory(invoice.id, 100);
      if (!response?.success) {
        throw new Error(response?.message || 'The status history could not be loaded');
      }
      setStatusHistoryInvoice(response.invoice || invoice);
      setStatusHistoryRows(Array.isArray(response.history) ? response.history : []);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setStatusHistoryError(backendMessage || err.message || 'The history could not be loaded');
    } finally {
      setStatusHistoryLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterProject('all');
    setFilterCategory('all');
    setFilterExpensePaymentStatus('all');
    setSearchTerm('');
    setStatsDateRange(getDefaultStatsDateRange());
  };

  const visibleExistingExpenseFiles = useMemo(() => {
    if (!expenseToEdit || !Array.isArray(expenseToEdit.receipt_files)) return [];
    return expenseToEdit.receipt_files.filter((storedPath) => !expenseRemovedFiles.includes(storedPath));
  }, [expenseToEdit, expenseRemovedFiles]);

  // --- Filtering Logic ---
  const filteredItems = useMemo(() => {
    const applyCommonFilters = (item, isExpense = false) => {
        const matchesProject = filterProject === 'all' || (item.project_id && item.project_id.toString() === filterProject);
        const matchesCategory = !isExpense || filterCategory === 'all' || item.category === filterCategory;
        const matchesPaymentStatus = !isExpense
          || filterExpensePaymentStatus === 'all'
          || ((item.payment_status || 'paid').toString().toLowerCase() === filterExpensePaymentStatus);
        return matchesProject && matchesCategory && matchesPaymentStatus;
    };

    if (currentTab === 3) { // Expenses
        return expenses.filter(exp => 
            exp.category !== 'Company Loan' && 
            (exp.title.toLowerCase().includes(searchTerm.toLowerCase()) || exp.category.toLowerCase().includes(searchTerm.toLowerCase())) &&
            applyCommonFilters(exp, true)
        );
    }
    
    if (currentTab === 5) { // Fixed Expenses
        return fixedExpenses.filter(exp => 
            (exp.title.toLowerCase().includes(searchTerm.toLowerCase()) || exp.category.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (filterCategory === 'all' || exp.category === filterCategory)
        );
    }
    
    if (currentTab === 6) { // Company Loans
        return expenses.filter(exp => 
            exp.category === 'Company Loan' && 
            (exp.title.toLowerCase().includes(searchTerm.toLowerCase())) &&
            applyCommonFilters(exp, true)
        );
    }

    // Invoices / Quotes / POs
    return invoices;
  }, [invoices, expenses, fixedExpenses, currentTab, filterProject, filterCategory, filterExpensePaymentStatus, searchTerm]);

  const paginatedItems = useMemo(() => {
    const isDocumentTab = ![TAB_VALUES.expenses, TAB_VALUES.fixedExpenses, TAB_VALUES.companyLoans].includes(currentTab);
    if (isDocumentTab) return filteredItems;
    const startIndex = (page - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, page, currentTab]);

  const totalPages = ![TAB_VALUES.expenses, TAB_VALUES.fixedExpenses, TAB_VALUES.companyLoans].includes(currentTab)
    ? Math.max(1, Number(documentPagination.totalPages || 1))
    : Math.ceil(filteredItems.length / itemsPerPage);
  const defaultStatsRange = getDefaultStatsDateRange();
  const hasActiveUiFilters = (
    filterStatus !== 'all'
    || filterProject !== 'all'
    || filterCategory !== 'all'
    || filterExpensePaymentStatus !== 'all'
    || searchTerm.trim().length > 0
    || statsDateRange.dateFrom !== defaultStatsRange.dateFrom
    || statsDateRange.dateTo !== defaultStatsRange.dateTo
  );
  const kpiRangeLabel = (
    (stats.filters?.date_from || stats.filters?.date_to)
      ? `${stats.filters?.date_from || '...'} to ${stats.filters?.date_to || '...'}`
      : 'All time'
  );

  // --- Components ---
  const StatCard = ({ title, value, icon: Icon, color, caption = '', metric, currency, rawValue }) => (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: alpha(color, 0.18), borderRadius: 2.5, height: '100%', background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`, transition: 'all 0.2s ease', '&:hover': { boxShadow: `0 10px 22px ${alpha(color, 0.16)}`, transform: 'translateY(-1px)', borderColor: alpha(color, 0.42) } }}>
        <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> 
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
                    {/* ✅ RESPONSIVE FONT SIZE */}
                    <Typography variant="h6" sx={{ fontWeight: 800, color: color, mt: 0.25, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
                    {caption && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                        {caption}
                      </Typography>
                    )}
                </Box>
                <Avatar sx={{ bgcolor: alpha(color, 0.1), width: 38, height: 38 }}><Icon sx={{ fontSize: 20, color }} /></Avatar>
            </Box> 
            {metric && hasPermission('billingDocuments', 'view') && <Button size="small" aria-label={`Ver desglose de ${title}`} onClick={() => setFinanceBreakdown({ metric, currency, title, expectedTotal: rawValue, dateFrom: stats.filters?.date_from || '', dateTo: stats.filters?.date_to || '' })} sx={{ mt: 1, minHeight: 44, textTransform: 'none' }}>Ver desglose</Button>}
        </CardContent> 
    </Card> 
  );
  
  const InvoiceRow = ({ invoice }) => {
    const statusConfig = getStatusConfigByType(invoice.type, invoice.status, statusLabelsByType);
    const normalizedStatus = (invoice.status || '').toString().toLowerCase().trim() || 'pending';
    const statusOptions = getRowTransitionStatusOptions({
      type: invoice.type,
      currentStatus: normalizedStatus,
      statusOptionsByType,
      statusRules
    });
    const isUpdatingStatus = Boolean(statusUpdatingById[invoice.id]);
    const isRunningRowAction = Boolean(rowActionLoadingById[invoice.id]);
    let typeLabel = 'INV';
    let typeColor = 'success';
    if (invoice.type === 'quote') {
      typeLabel = 'QT';
      typeColor = 'primary';
    }
    if (invoice.type === 'purchase_order') {
      typeLabel = 'PO';
      typeColor = 'warning';
    }

    return (
      <TableRow sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
        <TableCell>
          <Chip
            label={typeLabel}
            size="small"
            color={typeColor}
            variant="outlined"
            sx={{ fontWeight: 700, borderRadius: 1, height: 20, fontSize: '0.7rem' }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{invoice.invoice_number}</Typography>
          <Typography variant="caption" color="text.secondary">
            {invoice.issue_date ? format(new Date(invoice.issue_date), 'dd MMM yyyy') : '-'}
          </Typography>
        </TableCell>
        <TableCell><Typography variant="body2">{invoice.client_name}</Typography></TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {formatCurrency(invoice.total, invoice.currency || 'MXN')}
          </Typography>
          {Number(invoice.payment_count || 0) > 0 && (
            <Box sx={{ mt: 0.35 }}>
              {Number(invoice.incoming_paid || 0) > 0 && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 700 }}>
                  Received {formatCurrency(invoice.incoming_paid, invoice.currency || 'MXN')} / Balance {formatCurrency(Math.max(0, Number(invoice.total || 0) - Number(invoice.incoming_paid || 0)), invoice.currency || 'MXN')}
                </Typography>
              )}
              {Number(invoice.outgoing_paid || 0) > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 700 }}>
                  Paid {formatCurrency(invoice.outgoing_paid, invoice.currency || 'MXN')} / Balance {formatCurrency(Math.max(0, Number(invoice.total || 0) - Number(invoice.outgoing_paid || 0)), invoice.currency || 'MXN')}
                </Typography>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
            <FormControl size="small" fullWidth>
              <Select
                value={normalizedStatus}
                onChange={(e) => handleQuickStatusChange(invoice, e.target.value)}
                disabled={isUpdatingStatus || !canUpdateDocumentStatus}
                sx={{
                  color: statusConfig.color,
                  bgcolor: alpha(statusConfig.color, 0.08),
                  borderRadius: 2,
                  fontWeight: 600,
                  '.MuiSelect-select': { py: 0.8, fontSize: '0.8rem' }
                }}
              >
                {statusOptions.map((statusValue) => (
                  <MenuItem key={statusValue} value={statusValue}>
                    {getStatusConfigByType(invoice.type, statusValue, statusLabelsByType).label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isUpdatingStatus && <CircularProgress size={16} />}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Status History"><span><IconButton size="small" color="inherit" onClick={() => handleOpenStatusHistory(invoice)} disabled={isRunningRowAction}><HistoryIcon fontSize="small" /></IconButton></span></Tooltip>
            {invoice.type === 'quote' && <Tooltip title="Seguimiento comercial"><IconButton aria-label={`Seguimiento comercial ${invoice.invoice_number}`} onClick={() => setCommercialQuote(invoice)} sx={{ minWidth: 44, minHeight: 44 }}><ForumOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
            {canViewFinanceRecords && invoice.type !== 'quote' && (
              <Tooltip title="Payment installments"><span><IconButton size="small" color="success" onClick={() => handleOpenPaymentLedger(invoice)} disabled={isRunningRowAction}><PaymentIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            <Tooltip title="View Document"><span><IconButton size="small" color="primary" onClick={() => handleViewInvoiceSafe(invoice)} disabled={isRunningRowAction}><ViewIcon fontSize="small" /></IconButton></span></Tooltip>
            {canEditDocuments && (
              <Tooltip title="Edit"><span><IconButton size="small" color="info" onClick={() => handleOpenEditInvoice(invoice)} disabled={isRunningRowAction}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            {canCreateDocuments && (
              <Tooltip title="Duplicate"><span><IconButton size="small" color="secondary" onClick={() => handleDuplicateInvoice(invoice)} disabled={isRunningRowAction}><DuplicateIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            {canCreateDocuments && invoice.type === 'quote' && (
              <Tooltip title="Convert to Invoice"><span><IconButton size="small" color="warning" onClick={() => handleConvertQuoteToInvoice(invoice)} disabled={isRunningRowAction}><ConvertIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            {(canEditDocuments || canCreateDocuments || canUpdateDocumentStatus) && (
              <Tooltip title="Send Email"><span><IconButton size="small" color="primary" onClick={() => handleOpenSendInvoiceDialog(invoice)} disabled={isRunningRowAction}><EmailIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            {canDeleteDocuments && (
              <Tooltip title="Delete"><span><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: invoice.id, type: 'invoice', docType: invoice.type, title: invoice.invoice_number, subtitle: invoice.client_name, status: invoice.status, amount: invoice.total, currency: invoice.currency || 'MXN' })} disabled={isRunningRowAction}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
            )}
            <Tooltip title="Download PDF"><span><IconButton size="small" color="success" onClick={() => handleDownloadPDF(invoice)} disabled={isRunningRowAction}><DownloadIcon fontSize="small" /></IconButton></span></Tooltip>
            {invoice.file_path && (
              <Tooltip title="View Uploaded File">
                <IconButton size="small" color="secondary" onClick={() => handleOpenUploadedInvoiceFileSafe(invoice)}>
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isRunningRowAction && <CircularProgress size={16} />}
          </Box>
        </TableCell>
      </TableRow>
    );
  };
  const ExpenseRow = ({ expense }) => {
    const projectName = projectsById[String(expense.project_id)]?.name;
    const isLoan = expense.category === 'Company Loan';
    const isPaid = (expense.payment_status || 'paid') === 'paid';
    const receiptFiles = Array.isArray(expense.receipt_files)
      ? expense.receipt_files
      : (expense.receipt_path ? [expense.receipt_path] : []);

    return (
      <TableRow sx={{ '&:hover': { bgcolor: alpha(isLoan ? theme.palette.warning.main : theme.palette.error.main, 0.05) } }}>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{expense.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {expense.date ? format(new Date(expense.date), 'dd MMM yyyy') : '-'}
          </Typography>
          {projectName && (
            <Chip
              label={projectName}
              size="small"
              icon={<BusinessIcon style={{ fontSize: 12 }} />}
              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
            />
          )}
          {receiptFiles.length > 0 && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              {receiptFiles.slice(0, 3).map((receiptPath, idx) => (
                <Tooltip key={`${expense.id}-receipt-${idx}`} title={`Open attachment ${idx + 1}`}>
                  <IconButton size="small" onClick={() => handleOpenExpenseAttachmentSafe(expense.id, idx, receiptPath)}>
                    <ReceiptIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ))}
              {receiptFiles.length > 3 && (
                <Chip
                  size="small"
                  label={`+${receiptFiles.length - 3}`}
                  onClick={() => handleOpenExpenseAttachments(expense)}
                  sx={{ height: 18, cursor: 'pointer' }}
                />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={expense.category}
              size="small"
              variant="outlined"
              color={isLoan ? 'warning' : 'default'}
              icon={isLoan ? <BankIcon style={{ fontSize: 14 }} /> : <CategoryIcon style={{ fontSize: 14 }} />}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: isLoan ? 'warning.main' : 'error.main' }}>
            - {formatCurrency(expense.amount, expense.currency || 'MXN')}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={isPaid ? 'Paid' : 'Pending'}
            size="small"
            color={isPaid ? 'success' : 'warning'}
            variant={isPaid ? 'filled' : 'outlined'}
            sx={{ borderRadius: 2 }}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" color="info" onClick={() => handleOpenEditExpense(expense)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: expense.id, type: 'expense', title: expense.title, subtitle: expense.category, status: expense.payment_status || 'paid', amount: expense.amount, currency: expense.currency || 'MXN' })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  };
  const FixedExpenseRow = ({ expense }) => { return ( <TableRow sx={{ '&:hover': { bgcolor: alpha('#9c27b0', 0.05) } }}> <TableCell colSpan={2}> <Typography variant="body2" sx={{ fontWeight: 600 }}>{expense.title}</Typography> <Typography variant="caption" color="text.secondary">Due on day: {expense.day_of_month}</Typography> </TableCell> <TableCell> <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> <Chip label={expense.category} size="small" variant="outlined" color="secondary" /> </Box> </TableCell> <TableCell><Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'secondary.main' }}>{formatCurrency(expense.amount)} / mo</Typography></TableCell> <TableCell> <Tooltip title="Record Payment for this Month"> <Button size="small" variant="contained" color="secondary" startIcon={<ProcessIcon />} onClick={() => handleProcessFixedExpense(expense.id)} sx={{ borderRadius: 5, fontSize: '0.7rem', textTransform: 'none' }}> Record Payment </Button> </Tooltip> </TableCell> <TableCell> <Tooltip title="Delete Template"><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: expense.id, type: 'fixed_expense', title: expense.title, subtitle: expense.category, amount: expense.amount, currency: 'MXN' })}><DeleteIcon fontSize="small" /></IconButton></Tooltip> </TableCell> </TableRow> ); };

  const MobileInvoiceCard = ({ invoice }) => {
    const normalizedStatus = (invoice.status || '').toString().toLowerCase().trim() || 'pending';
    const statusConfig = getStatusConfigByType(invoice.type, normalizedStatus, statusLabelsByType);
    const statusOptions = getRowTransitionStatusOptions({ type: invoice.type, currentStatus: normalizedStatus, statusOptionsByType, statusRules });
    const busy = Boolean(rowActionLoadingById[invoice.id]);
    const typeLabel = invoice.type === 'quote' ? 'QT' : invoice.type === 'purchase_order' ? 'PO' : 'INV';
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.7} alignItems="center"><Chip size="small" variant="outlined" label={typeLabel} /><Typography variant="subtitle2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>{invoice.invoice_number}</Typography></Stack>
            <Typography variant="caption" color="text.secondary">{invoice.issue_date ? format(new Date(invoice.issue_date), 'dd MMM yyyy') : '-'}</Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight={900} color="primary.main" sx={{ flexShrink: 0 }}>{formatCurrency(invoice.total, invoice.currency || 'MXN')}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ mt: 1.1, overflowWrap: 'anywhere' }}>{invoice.client_name || 'No client'}</Typography>
        {(Number(invoice.incoming_paid || 0) > 0 || Number(invoice.outgoing_paid || 0) > 0) && (
          <Typography variant="caption" color={Number(invoice.incoming_paid || 0) > 0 ? 'success.main' : 'warning.main'} sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
            {Number(invoice.incoming_paid || 0) > 0 ? `Received ${formatCurrency(invoice.incoming_paid, invoice.currency || 'MXN')}` : `Paid ${formatCurrency(invoice.outgoing_paid, invoice.currency || 'MXN')}`}
          </Typography>
        )}
        <FormControl size="small" fullWidth sx={{ mt: 1.3 }}>
          <Select value={normalizedStatus} onChange={(event) => handleQuickStatusChange(invoice, event.target.value)} disabled={Boolean(statusUpdatingById[invoice.id]) || !canUpdateDocumentStatus} sx={{ color: statusConfig.color, bgcolor: alpha(statusConfig.color, 0.08), fontWeight: 700 }}>
            {statusOptions.map((statusValue) => <MenuItem key={statusValue} value={statusValue}>{getStatusConfigByType(invoice.type, statusValue, statusLabelsByType).label}</MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.1 }}>
          <IconButton size="small" onClick={() => handleOpenStatusHistory(invoice)} disabled={busy} aria-label="Status history"><HistoryIcon fontSize="small" /></IconButton>
          {invoice.type === 'quote' && <IconButton aria-label={`Seguimiento comercial ${invoice.invoice_number}`} onClick={() => setCommercialQuote(invoice)} sx={{ minWidth: 44, minHeight: 44 }}><ForumOutlinedIcon fontSize="small" /></IconButton>}
          {canViewFinanceRecords && invoice.type !== 'quote' && <IconButton size="small" color="success" onClick={() => handleOpenPaymentLedger(invoice)} disabled={busy} aria-label="Payments"><PaymentIcon fontSize="small" /></IconButton>}
          <IconButton size="small" color="primary" onClick={() => handleViewInvoiceSafe(invoice)} disabled={busy} aria-label="View"><ViewIcon fontSize="small" /></IconButton>
          {canEditDocuments && <IconButton size="small" color="info" onClick={() => handleOpenEditInvoice(invoice)} disabled={busy} aria-label="Edit"><EditIcon fontSize="small" /></IconButton>}
          {canCreateDocuments && <IconButton size="small" color="secondary" onClick={() => handleDuplicateInvoice(invoice)} disabled={busy} aria-label="Duplicate"><DuplicateIcon fontSize="small" /></IconButton>}
          {canCreateDocuments && invoice.type === 'quote' && <IconButton size="small" color="warning" onClick={() => handleConvertQuoteToInvoice(invoice)} disabled={busy} aria-label="Convert to invoice"><ConvertIcon fontSize="small" /></IconButton>}
          {(canEditDocuments || canCreateDocuments || canUpdateDocumentStatus) && <IconButton size="small" color="primary" onClick={() => handleOpenSendInvoiceDialog(invoice)} disabled={busy} aria-label="Send email"><EmailIcon fontSize="small" /></IconButton>}
          <IconButton size="small" color="success" onClick={() => handleDownloadPDF(invoice)} disabled={busy} aria-label="Download PDF"><DownloadIcon fontSize="small" /></IconButton>
          {invoice.file_path && <IconButton size="small" color="secondary" onClick={() => handleOpenUploadedInvoiceFileSafe(invoice)} aria-label="Uploaded file"><AttachFileIcon fontSize="small" /></IconButton>}
          {canDeleteDocuments && <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: invoice.id, type: 'invoice', docType: invoice.type, title: invoice.invoice_number, subtitle: invoice.client_name, status: invoice.status, amount: invoice.total, currency: invoice.currency || 'MXN' })} disabled={busy} aria-label="Delete"><DeleteIcon fontSize="small" /></IconButton>}
          {busy && <CircularProgress size={16} />}
        </Stack>
      </Paper>
    );
  };

  const MobileExpenseCard = ({ expense }) => {
    const projectName = projectsById[String(expense.project_id)]?.name;
    const isPaid = (expense.payment_status || 'paid') === 'paid';
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}><Typography variant="subtitle2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>{expense.title}</Typography><Typography variant="caption" color="text.secondary">{expense.date ? format(new Date(expense.date), 'dd MMM yyyy') : '-'}</Typography></Box>
          <Typography variant="subtitle2" fontWeight={900} color="error.main" sx={{ flexShrink: 0 }}>- {formatCurrency(expense.amount, expense.currency || 'MXN')}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}><Chip size="small" variant="outlined" label={expense.category} /><Chip size="small" color={isPaid ? 'success' : 'warning'} label={isPaid ? 'Paid' : 'Pending'} />{projectName && <Chip size="small" label={projectName} />}</Stack>
        <Stack direction="row" spacing={0.7} sx={{ mt: 1.2 }}><Button fullWidth size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenEditExpense(expense)}>Edit</Button><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: expense.id, type: 'expense', title: expense.title, subtitle: expense.category, status: expense.payment_status || 'paid', amount: expense.amount, currency: expense.currency || 'MXN' })}><DeleteIcon fontSize="small" /></IconButton></Stack>
      </Paper>
    );
  };

  const MobileFixedExpenseCard = ({ expense }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}><Box sx={{ minWidth: 0 }}><Typography variant="subtitle2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>{expense.title}</Typography><Typography variant="caption" color="text.secondary">Due on day {expense.day_of_month}</Typography></Box><Typography variant="subtitle2" fontWeight={900} color="secondary.main">{formatCurrency(expense.amount)} / mo</Typography></Stack>
      <Chip size="small" variant="outlined" color="secondary" label={expense.category} sx={{ mt: 1.2 }} />
      <Stack direction="row" spacing={0.7} sx={{ mt: 1.2 }}><Button fullWidth size="small" variant="contained" color="secondary" startIcon={<ProcessIcon />} onClick={() => handleProcessFixedExpense(expense.id)}>Record Payment</Button><IconButton size="small" color="error" onClick={() => setDeleteConfirm({ id: expense.id, type: 'fixed_expense', title: expense.title, subtitle: expense.category, amount: expense.amount, currency: 'MXN' })}><DeleteIcon fontSize="small" /></IconButton></Stack>
    </Paper>
  );

  if (loading && !hasLoadedOnce) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;

  const financeAccent = documentsOnly ? theme.palette.warning.main : theme.palette.primary.main;
  const financeAccentDark = documentsOnly ? theme.palette.warning.dark : theme.palette.primary.dark;
  const deleteTypeLabel = deleteConfirm?.type === 'expense'
    ? 'Expense'
    : deleteConfirm?.type === 'fixed_expense'
      ? 'Fixed Expense Template'
      : deleteConfirm?.docType === 'quote'
        ? 'Quote'
        : deleteConfirm?.docType === 'purchase_order'
          ? 'Purchase Order'
          : 'Invoice';
  const deleteStatusLabel = deleteConfirm?.type === 'invoice'
    ? getStatusConfigByType(deleteConfirm?.docType || 'invoice', deleteConfirm?.status || '', statusLabelsByType).label
    : deleteConfirm?.type === 'expense'
      ? ((deleteConfirm?.status || 'paid') === 'paid' ? 'Paid' : 'Pending payment')
      : '';
  const currentViewLabel = currentTab === TAB_VALUES.quotes
    ? 'Quotes'
    : currentTab === TAB_VALUES.invoices
      ? 'Invoices'
      : currentTab === TAB_VALUES.purchaseOrders
        ? 'Purchase Orders'
        : currentTab === TAB_VALUES.expenses
          ? 'Expenses'
          : currentTab === TAB_VALUES.fixedExpenses
            ? 'Fixed Expenses'
            : currentTab === TAB_VALUES.companyLoans
              ? 'Company Loans'
              : 'All Records';

  return (
    <Box sx={{ p: 0 }}>
      
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.4, md: 1.7 },
            mb: 1.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha(financeAccent, 0.24),
            color: 'common.white',
            background: `linear-gradient(135deg, ${financeAccentDark} 0%, ${financeAccent} 55%, ${alpha(financeAccent, 0.82)} 100%)`,
            boxShadow: `0 10px 24px ${alpha(financeAccentDark, 0.2)}`
          }}
        >
          {/* ✅ HEADER RESPONSIVO: Column en móvil, Row en desktop */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: alpha('#fff', 0.18),
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.24)
                }}
              >
                {documentsOnly ? <QuoteIcon sx={{ fontSize: 23 }} /> : <MoneyIcon sx={{ fontSize: 23 }} />}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {pageTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.84), mt: 0.5 }}>
                  {documentsOnly ? 'Invoices, quotes, and purchase orders.' : 'Income, expenses, payments, and financial tracking.'}
                </Typography>
                <Box sx={{ mt: .7, display: 'flex', gap: .7, flexWrap: 'wrap' }}>
                  <Chip size="small" label={currentViewLabel} sx={{ bgcolor: alpha('#fff', 0.16), color: 'common.white', fontWeight: 700 }} />
                  <Chip size="small" label={`Rango KPI: ${kpiRangeLabel}`} sx={{ bgcolor: alpha('#fff', 0.12), color: 'common.white' }} />
                  {hasActiveUiFilters && (
                    <Chip size="small" label="Active filters" sx={{ bgcolor: alpha('#fff', 0.16), color: 'common.white' }} />
                  )}
                </Box>
              </Box>
            </Box>

            {/* ✅ BOTONES RESPONSIVOS: Wrap para que bajen si no caben */}
            <Box sx={{ display: 'flex', gap: .8, width: { xs: '100%', lg: 'auto' }, justifyContent: { xs: 'stretch', lg: 'flex-end' }, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExportDataset('csv')} sx={{ color: 'common.white', borderColor: alpha('#fff', 0.42), '&:hover': { borderColor: alpha('#fff', 0.8), bgcolor: alpha('#fff', 0.08) } }}>CSV</Button>
                <Button size="small" variant="outlined" startIcon={<TableChartIcon />} onClick={() => handleExportDataset('excel')} sx={{ color: 'common.white', borderColor: alpha('#fff', 0.42), '&:hover': { borderColor: alpha('#fff', 0.8), bgcolor: alpha('#fff', 0.08) } }}>Excel</Button>
                {!documentsOnly && canCreateFinanceRecords && (
                  <Button size="small" variant="outlined" startIcon={<RepeatIcon />} onClick={handleOpenCreateFixedExpense} sx={{ color: 'common.white', borderColor: alpha('#fff', 0.42), display: {xs: 'none', sm: 'flex'}, '&:hover': { borderColor: alpha('#fff', 0.8), bgcolor: alpha('#fff', 0.08) } }}>Fixed Expense</Button>
                )}
                {!documentsOnly && canCreateFinanceRecords && (
                  <Button size="small" variant="outlined" startIcon={<MoneyOffIcon />} onClick={handleOpenCreateExpense} sx={{ color: 'common.white', borderColor: alpha('#fff', 0.42), '&:hover': { borderColor: alpha('#fff', 0.8), bgcolor: alpha('#fff', 0.08) } }}>New Expense</Button>
                )}
                {canCreateDocuments && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateInvoice} sx={{ bgcolor: 'common.white', color: financeAccentDark, fontWeight: 700, boxShadow: `0 8px 18px ${alpha('#000', 0.14)}`, '&:hover': { bgcolor: alpha('#fff', 0.92) } }}>New Document</Button>
                )}
            </Box>
          </Box>
        </Paper>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(financeAccent, 0.15),
          bgcolor: alpha(theme.palette.background.paper, 0.86)
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Vista: ${currentViewLabel}`} sx={{ fontWeight: 700, bgcolor: alpha(financeAccent, 0.12), color: financeAccentDark }} />
            <Chip size="small" label={`${paginatedItems.length} visibles`} variant="outlined" />
            {!documentsOnly && (
              <Chip size="small" label={`Net MXN: ${formatCurrency(stats.MXN.netProfit, 'MXN')}`} variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', lg: 'flex-end' } }}>
            {!documentsOnly && (
              <Button
                size="small"
                variant={showKpis ? 'contained' : 'outlined'}
                onClick={() => setShowKpis((prev) => !prev)}
                endIcon={showKpis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Indicators
              </Button>
            )}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters((prev) => !prev)}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Filters
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ✅ KPIS (MXN & USD) CON 5 TARJETAS PERFECTAMENTE ALINEADAS */}
      {!documentsOnly && (
      <Collapse in={showKpis}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          Indicator period: {kpiRangeLabel}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: .7, color: 'text.secondary', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}> <CurrencyIcon fontSize="small" /> FINANCE (MXN) </Typography>
        <Grid container spacing={1.2} sx={{ mb: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, '& > .MuiGrid-root': { width: 'auto', minWidth: 0 } }}>
            <Grid item><StatCard title="Net Profit (MXN)" value={formatCurrency(stats.MXN.netProfit, 'MXN')} icon={MoneyIcon} color={stats.MXN.netProfit >= 0 ? "#2e7d32" : "#d32f2f"} metric="net_profit" currency="MXN" rawValue={stats.MXN.netProfit} /></Grid>
            <Grid item><StatCard title="Revenue (MXN)" value={formatCurrency(stats.MXN.totalRevenue, 'MXN')} icon={TrendingUpIcon} color="#1976d2" caption="Cobrado, sin IVA trasladado de facturas México" metric="revenue" currency="MXN" rawValue={stats.MXN.totalRevenue} /></Grid>
            <Grid item><StatCard title="Expenses (MXN)" value={formatCurrency(stats.MXN.totalExpenses, 'MXN')} icon={MoneyOffIcon} color="#d32f2f" metric="expenses" currency="MXN" rawValue={stats.MXN.totalExpenses} /></Grid>
            <Grid item><StatCard title="Pending (MXN)" value={formatCurrency(stats.MXN.pendingAmount, 'MXN')} icon={PaymentIcon} color="#ed6c02" metric="pending" currency="MXN" rawValue={stats.MXN.pendingAmount} /></Grid>
            <Grid item><StatCard title="Loans (MXN)" value={formatCurrency(stats.Loans.MXN, 'MXN')} icon={BankIcon} color="#9c27b0" metric="loans" currency="MXN" rawValue={stats.Loans.MXN} /></Grid>
            <Grid item><StatCard title="Total de invoices (MXN)" value={formatCurrency(stats.MXN.invoicesAmount, 'MXN')} icon={ReceiptIcon} color={theme.palette.primary.main} caption="Total documental con impuestos; todos los estatus" metric="invoiced" currency="MXN" rawValue={stats.MXN.invoicesAmount} /></Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mb: .7, color: 'text.secondary', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}> <CurrencyIcon fontSize="small" /> FINANCE (USD) </Typography>
        <Grid container spacing={1.2} sx={{ mb: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, '& > .MuiGrid-root': { width: 'auto', minWidth: 0 } }}>
            <Grid item><StatCard title="Net Profit (USD)" value={formatCurrency(stats.USD.netProfit, 'USD')} icon={MoneyIcon} color={stats.USD.netProfit >= 0 ? "#2e7d32" : "#d32f2f"} metric="net_profit" currency="USD" rawValue={stats.USD.netProfit} /></Grid>
            <Grid item><StatCard title="Revenue (USD)" value={formatCurrency(stats.USD.totalRevenue, 'USD')} icon={TrendingUpIcon} color="#1976d2" caption="Cobrado, sin IVA trasladado de facturas México" metric="revenue" currency="USD" rawValue={stats.USD.totalRevenue} /></Grid>
            <Grid item><StatCard title="Expenses (USD)" value={formatCurrency(stats.USD.totalExpenses, 'USD')} icon={MoneyOffIcon} color="#d32f2f" metric="expenses" currency="USD" rawValue={stats.USD.totalExpenses} /></Grid>
            <Grid item><StatCard title="Pending (USD)" value={formatCurrency(stats.USD.pendingAmount, 'USD')} icon={PaymentIcon} color="#ed6c02" metric="pending" currency="USD" rawValue={stats.USD.pendingAmount} /></Grid>
            <Grid item><StatCard title="Loans (USD)" value={formatCurrency(stats.Loans.USD, 'USD')} icon={BankIcon} color="#9c27b0" metric="loans" currency="USD" rawValue={stats.Loans.USD} /></Grid>
            <Grid item><StatCard title="Total de invoices (USD)" value={formatCurrency(stats.USD.invoicesAmount, 'USD')} icon={ReceiptIcon} color={theme.palette.primary.main} caption="Total documental con impuestos; todos los estatus" metric="invoiced" currency="USD" rawValue={stats.USD.invoicesAmount} /></Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha(theme.palette.info.main, 0.24),
            bgcolor: alpha(theme.palette.info.main, 0.045)
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, mb: 1.2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <ReceiptIcon fontSize="small" color="info" aria-hidden="true" />
                IVA COBRADO · MÉXICO
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, overflowWrap: 'anywhere' }}>
                {stats.VAT?.issuerName || 'SMTLINK SOLUTIONS, S.A. de C.V.'} · calculado proporcionalmente sobre cobros recibidos.
              </Typography>
            </Box>
            <Chip size="small" color="info" variant="outlined" label="LLC excluida" />
          </Box>

          <Grid container spacing={1.2} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, '& > .MuiGrid-root': { width: 'auto', minWidth: 0 } }}>
            <Grid item>
              <StatCard
                title="IVA cobrado (MXN)"
                metric="vat" currency="MXN" rawValue={stats.VAT?.MXN?.vatCollected || 0}
                value={formatCurrency(stats.VAT?.MXN?.vatCollected || 0, 'MXN')}
                icon={ReceiptIcon}
                color={theme.palette.info.main}
                caption={`${Number(stats.VAT?.MXN?.documentCount || 0)} invoices · cobros considerados ${formatCurrency(stats.VAT?.MXN?.grossCollected || 0, 'MXN')}`}
              />
            </Grid>
            <Grid item>
              <StatCard
                title="IVA cobrado (USD)"
                metric="vat" currency="USD" rawValue={stats.VAT?.USD?.vatCollected || 0}
                value={formatCurrency(stats.VAT?.USD?.vatCollected || 0, 'USD')}
                icon={ReceiptIcon}
                color={theme.palette.info.main}
                caption={`${Number(stats.VAT?.USD?.documentCount || 0)} invoices · cobros considerados ${formatCurrency(stats.VAT?.USD?.grossCollected || 0, 'USD')}`}
              />
            </Grid>
          </Grid>

          {(Number(stats.VAT?.unassigned?.MXN?.documentCount || 0) + Number(stats.VAT?.unassigned?.USD?.documentCount || 0)) > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1.2 }}>
              Se excluyeron {Number(stats.VAT?.unassigned?.MXN?.documentCount || 0) + Number(stats.VAT?.unassigned?.USD?.documentCount || 0)} invoice(s) cobradas sin razón social asignada. Edita esos documentos y selecciona México o USA antes de conciliarlos.
            </Alert>
          )}
          {(Number(stats.VAT?.MXN?.legacyPaidCount || 0) + Number(stats.VAT?.USD?.legacyPaidCount || 0)) > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 1.2 }}>
              Incluye {Number(stats.VAT?.MXN?.legacyPaidCount || 0) + Number(stats.VAT?.USD?.legacyPaidCount || 0)} invoice(s) antiguas marcadas como pagadas sin movimientos de cobro. Se estiman al 100% usando la fecha del documento.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            El periodo usa la fecha de cada cobro registrado. Este indicador es de control interno y debe conciliarse con CFDI y complementos de pago.
          </Typography>
        </Paper>
      </Box>
      </Collapse>
      )}

      {/* Tabs & Filters */}
      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2.5, border: '1px solid', borderColor: alpha(financeAccent, 0.15), overflow: 'hidden', bgcolor: alpha(theme.palette.background.paper, 0.88) }}>
        <Tabs value={currentTab} onChange={(e, v) => { setCurrentTab(v); setPage(1); }} sx={{ borderBottom: '1px solid', borderColor: alpha(financeAccent, 0.12), px: 1, '& .MuiTab-root': { minHeight: 46, textTransform: 'none', fontWeight: 700 } }} variant="scrollable" scrollButtons="auto">
          <Tab value={TAB_VALUES.allDocs} label="All Documents" />
          {canViewInvoiceDocs && <Tab value={TAB_VALUES.invoices} label="Invoices" />}
          {canViewQuoteDocs && <Tab value={TAB_VALUES.quotes} label="Quotes" />}
          {!documentsOnly && <Tab value={TAB_VALUES.expenses} label="Expenses" icon={<MoneyOffIcon fontSize="small"/>} iconPosition="start" />}
          {canViewPurchaseOrderDocs && <Tab value={TAB_VALUES.purchaseOrders} label="Purchase Orders" icon={<ShoppingCartIcon fontSize="small"/>} iconPosition="start" />}
          {!documentsOnly && <Tab value={TAB_VALUES.fixedExpenses} label="Fixed Expenses" icon={<RepeatIcon fontSize="small"/>} iconPosition="start" />}
          {!documentsOnly && <Tab value={TAB_VALUES.companyLoans} label="Company Loans" icon={<BankIcon fontSize="small"/>} iconPosition="start" />}
        </Tabs>
        
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {/* ✅ FILTROS RESPONSIVOS */}
          <Grid container spacing={1.2} alignItems="center" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(220px, 1fr)' }, '& > .MuiGrid-root': { width: 'auto', minWidth: 0 } }}>
            <Grid item>
                <TextField fullWidth size="small" placeholder="Search client, title, or document number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><ViewIcon color="action" /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.72) } }} />
            </Grid>
            <Grid item sx={{ display: 'flex', justifyContent: { xs: 'space-between', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
                <Button variant={showFilters ? "contained" : "outlined"} startIcon={<FilterIcon />} onClick={() => setShowFilters(!showFilters)} size="small" fullWidth={isMobile} sx={{ textTransform: 'none', fontWeight: 700 }}>Filters</Button>
                {hasActiveUiFilters && ( <Button variant="text" color="error" startIcon={<FilterOffIcon />} onClick={clearFilters} size="small" fullWidth={isMobile}>Clear</Button> )}
            </Grid>
            <Grid item sx={{ gridColumn: '1 / -1' }}>
                <Collapse in={showFilters}>
                    <Grid container spacing={1.2} sx={{ pt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, '& > .MuiGrid-root': { width: 'auto', minWidth: 0 } }}>
                        {(currentTab !== TAB_VALUES.expenses && currentTab !== TAB_VALUES.fixedExpenses && currentTab !== TAB_VALUES.companyLoans) && (
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                                      <MenuItem value="all">All Statuses</MenuItem>
                                      {getStatusFilterOptions(currentTab, statusOptionsByType).map((statusValue) => {
                                        const statusLabel = getStatusConfigByType(
                                          currentTab === TAB_VALUES.quotes ? 'quote' : currentTab === TAB_VALUES.purchaseOrders ? 'purchase_order' : 'invoice',
                                          statusValue,
                                          statusLabelsByType
                                        ).label;
                                        return <MenuItem key={statusValue} value={statusValue}>{statusLabel}</MenuItem>;
                                      })}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item>
                            <FormControl fullWidth size="small">
                                <InputLabel>Project</InputLabel>
                                <Select value={filterProject} label="Project" onChange={(e) => setFilterProject(e.target.value)}> <MenuItem value="all">All Projects</MenuItem> {projects.map((p) => ( <MenuItem key={p.id} value={p.id.toString()}>{p.name}</MenuItem> ))} </Select>
                            </FormControl>
                        </Grid>
                        {!documentsOnly && (currentTab === TAB_VALUES.expenses || currentTab === TAB_VALUES.companyLoans) && (
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Category</InputLabel>
                                    <Select value={filterCategory} label="Category" onChange={(e) => setFilterCategory(e.target.value)}> <MenuItem value="all">All Categories</MenuItem> {EXPENSE_CATEGORIES.map((cat) => ( <MenuItem key={cat} value={cat}>{cat}</MenuItem> ))} </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {!documentsOnly && (currentTab === TAB_VALUES.expenses || currentTab === TAB_VALUES.companyLoans) && (
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Payment</InputLabel>
                                    <Select value={filterExpensePaymentStatus} label="Payment" onChange={(e) => setFilterExpensePaymentStatus(e.target.value)}>
                                      <MenuItem value="all">All payments</MenuItem>
                                      <MenuItem value="paid">Paid</MenuItem>
                                      <MenuItem value="pending">Pending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {!documentsOnly && (
                          <>
                            <Grid item>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="KPI Date From"
                                value={statsDateRange.dateFrom}
                                onChange={(e) => setStatsDateRange((prev) => ({ ...prev, dateFrom: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                helperText="KPI only. Empty = complete history"
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="KPI Date To"
                                value={statsDateRange.dateTo}
                                onChange={(e) => setStatsDateRange((prev) => ({ ...prev, dateTo: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                helperText="KPI only. Empty = complete history"
                              />
                            </Grid>
                          </>
                        )}
                    </Grid>
                </Collapse>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Card Prestamo (Pestaña 6) */}
      {!documentsOnly && currentTab === TAB_VALUES.companyLoans && (
        <Fade in timeout={500}>
            <Box sx={{ mb: 3 }}>
                <Card sx={{ border: '1px dashed #ed6c02', bgcolor: alpha('#ed6c02', 0.05), borderRadius: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 2, mb: 2, textAlign: { xs: 'center', md: 'left' } }}>
                            <Avatar sx={{ bgcolor: '#ed6c02' }}><BankIcon sx={{ color: 'white' }} /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ed6c02' }}>Company Loan Tracker</Typography>
                                <Typography variant="caption" color="text.secondary">Expenses tagged as "Company Loan" within KPI range: {kpiRangeLabel}</Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}> <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>TOTAL LOAN DEBT (MXN)</Typography> <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#ed6c02' }}>{formatCurrency(stats.Loans.MXN, 'MXN')}</Typography> </Grid>
                            <Grid item xs={12} sm={6}> <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>TOTAL LOAN DEBT (USD)</Typography> <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#ed6c02' }}>{formatCurrency(stats.Loans.USD, 'USD')}</Typography> </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Fade>
      )}

      <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : paginatedItems.length === 0 ? <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}><Typography color="text.secondary">No records found</Typography></Paper> : paginatedItems.map((item) => (
          (currentTab === TAB_VALUES.expenses || currentTab === TAB_VALUES.companyLoans)
            ? <MobileExpenseCard key={`mobile-finance-${item.id}`} expense={item} />
            : currentTab === TAB_VALUES.fixedExpenses
              ? <MobileFixedExpenseCard key={`mobile-finance-${item.id}`} expense={item} />
              : <MobileInvoiceCard key={`mobile-finance-${item.id}`} invoice={item} />
        ))}
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {currentTab === 3 || currentTab === 6 ? ( <><TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></> ) : currentTab === 5 ? ( <><TableCell colSpan={2}>Recurring Template</TableCell><TableCell>Category</TableCell><TableCell>Amount</TableCell><TableCell>Action</TableCell><TableCell>Manage</TableCell></> ) : ( <> <TableCell sx={{fontWeight:700}}>Type</TableCell> <TableCell sx={{fontWeight:700}}>Number</TableCell> <TableCell sx={{fontWeight:700}}>Client</TableCell> <TableCell sx={{fontWeight:700}}>Amount</TableCell> <TableCell sx={{fontWeight:700}}>Status</TableCell> <TableCell sx={{fontWeight:700}}>Actions</TableCell> </> )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} align="center"><CircularProgress /></TableCell></TableRow> : paginatedItems.length === 0 ? <TableRow><TableCell colSpan={6} align="center">No records found</TableCell></TableRow> : paginatedItems.map((item) => ( (currentTab === 3 || currentTab === 6) ? <ExpenseRow key={item.id} expense={item} /> : currentTab === 5 ? <FixedExpenseRow key={item.id} expense={item} /> : <InvoiceRow key={item.id} invoice={item} /> ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && ( <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}> <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" /> </Box> )}

      {/* Dialogs */}
      <InvoiceFormDialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)} onSave={handleSaveInvoice} invoice={invoiceToEdit} formatCurrency={formatCurrency} nextDocumentNumbers={invoiceNumberSeedDocs} projectOptions={projects} />
      {financeBreakdown && <FinanceBreakdownDialog key={`${financeBreakdown.metric}:${financeBreakdown.currency}:${financeBreakdown.dateFrom}:${financeBreakdown.dateTo}`} selection={financeBreakdown} onClose={() => setFinanceBreakdown(null)} onDocument={(row) => { setFinanceBreakdown(null); handleViewInvoiceSafe({ ...row, invoice_number: row.title }); }} />}
      {false && <Dialog open={emailDialogOpen} onClose={() => !sendingInvoiceEmail && setEmailDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Send Document Email {emailTargetInvoice?.invoice_number ? `- ${emailTargetInvoice.invoice_number}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="To"
                value={emailForm.to}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                placeholder="client@example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CC (Optional)"
                value={emailForm.cc}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
                placeholder="team@example.com; sales@example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Message"
                value={emailForm.message}
                onChange={(e) => setEmailForm((prev) => ({ ...prev, message: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                If the document has an official attachment, it will be included. Otherwise, the email will contain a document summary.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingInvoiceEmail}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sendingInvoiceEmail ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            onClick={handleSendInvoiceEmail}
            disabled={sendingInvoiceEmail || !emailForm.to.trim()}
          >
            {sendingInvoiceEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>}
      {commercialQuote && <QuoteCommercialDialog
        key={commercialQuote.id}
        quote={commercialQuote}
        onClose={() => setCommercialQuote(null)}
        onOpenDraft={handleOpenSendInvoiceDialog}
        canManageDrafts={hasPermission('billingDocuments', 'edit') || hasPermission('billingDocuments', 'approve')}
        canCreateDrafts={hasPermission('billingDocuments', 'create') || hasPermission('billingDocuments', 'edit') || hasPermission('billingDocuments', 'approve')}
        canViewEmail={hasPermission('emailCenter', 'view')}
      />}
      <EmailComposerDialog
        open={emailDialogOpen}
        onClose={handleCloseInvoiceEmailDialog}
        onSend={handleSendInvoiceEmail}
        sending={sendingInvoiceEmail}
        title="Send Document Email"
        subtitle={`${getDocumentEmailLabel(emailTargetInvoice?.type)} ${emailTargetInvoice?.invoice_number || ''}`.trim()}
        badge={getDocumentEmailLabel(emailTargetInvoice?.type).toUpperCase()}
        form={emailForm}
        onFormChange={setEmailForm}
        template={emailTemplate}
        templateOptions={DOCUMENT_EMAIL_TEMPLATES}
        onTemplateChange={handleApplyInvoiceEmailTemplate}
        quickInserts={DOCUMENT_EMAIL_INSERTS}
        onQuickInsert={handleInsertInvoiceEmailSnippet}
        onResetDraft={() => handleApplyInvoiceEmailTemplate(emailTemplate)}
        senderLabel={OUTGOING_EMAIL_FROM}
        replyToEmail={replyToEmail}
        contextLabel="DOCUMENT"
        contextValue={emailTargetInvoice?.invoice_number || '-'}
        senderName={currentUserDisplayName}
        previewText={emailForm.message}
        summaryTitle="Document Summary"
        summaryItems={[
          { label: 'Type', value: getDocumentEmailLabel(emailTargetInvoice?.type) },
          ...(emailTargetInvoice?.type === 'purchase_order' ? [
            { label: 'Supplier', value: getDocumentNotesMeta(emailTargetInvoice?.notes).supplier_name },
            { label: 'Contact', value: getDocumentNotesMeta(emailTargetInvoice?.notes).supplier_contact }
          ] : [
            { label: 'Client', value: emailTargetInvoice?.client_name },
            { label: 'Contact', value: emailTargetInvoice?.client_contact }
          ]),
          { label: 'Total', value: emailTargetInvoice ? formatCurrency(emailTargetInvoice.total, emailTargetInvoice.currency || 'MXN') : '-' }
        ]}
        attachments={emailAttachments}
        attachmentLoading={emailAttachmentLoading}
        attachmentError={emailAttachmentError}
        onFilesSelected={handleInvoiceEmailFilesSelected}
        onRemoveAttachment={handleRemoveInvoiceEmailAttachment}
        history={emailHistory}
        historyLoading={emailHistoryLoading}
        historyError={emailHistoryError}
        onRefreshHistory={() => loadInvoiceEmailHistory(emailTargetInvoice?.id)}
        workflowStatus={emailTargetInvoice?.type === 'quote' ? (quoteEmailDraft?.status || 'draft') : null}
        workflowMessage={emailTargetInvoice?.type === 'quote'
          ? (quoteEmailDraft?.status === 'pending_review'
            ? `Submitted by ${quoteEmailDraft.submitted_by_name || 'a team member'}. The email cannot be sent until it is approved.`
            : quoteEmailDraft?.status === 'sending'
              ? 'Another user or process is currently sending this approved email.'
            : quoteEmailDraft?.status === 'approved'
              ? `Approved by ${quoteEmailDraft.approved_by_name || 'an authorized reviewer'}. After sending, a follow-up draft will be prepared in ${quoteEmailDraft.follow_up_days || 7} days only if no reply is detected.`
              : quoteEmailDraft?.status === 'rejected'
                ? (quoteEmailDraft.rejection_reason || 'The reviewer requested changes.')
                : 'Review the AI-generated text, edit it if necessary, then request approval.')
          : ''}
        workflowError={quoteEmailWorkflowError}
        workflowLoading={quoteEmailWorkflowLoading}
        workflowActions={emailTargetInvoice?.type !== 'quote' ? [] : [
          ...(['draft', 'rejected'].includes(quoteEmailDraft?.status) ? [{
            label: 'Request approval',
            variant: 'contained',
            onClick: handleSubmitQuoteEmailDraft,
            disabled: !quoteEmailDraft
          }] : []),
          ...(quoteEmailDraft?.status === 'pending_review' && canApproveDocuments ? [{
            label: 'Request changes',
            color: 'warning',
            onClick: handleRejectQuoteEmailDraft
          }, {
            label: 'Approve email',
            variant: 'contained',
            color: 'success',
            onClick: handleApproveQuoteEmailDraft
          }] : [])
        ]}
        sendDisabled={emailTargetInvoice?.type === 'quote' && quoteEmailDraft?.status !== 'approved'}
        sendLabel={emailTargetInvoice?.type === 'quote' ? 'Send approved quote' : 'Send Email'}
        formReadOnly={emailTargetInvoice?.type === 'quote' && (
          quoteEmailDraft?.status === 'approved'
          || quoteEmailDraft?.status === 'sending'
          || (quoteEmailDraft?.status === 'pending_review' && !canApproveDocuments)
        )}
        showFollowUpSettings={emailTargetInvoice?.type === 'quote' && quoteEmailDraft?.generation_source !== 'followup'}
        followUpDays={quoteEmailDraft?.follow_up_days || 7}
        onFollowUpDaysChange={(days) => setQuoteEmailDraft((current) => current ? ({ ...current, follow_up_days: days }) : current)}
      />
      <Dialog open={expenseDialogOpen} onClose={() => setExpenseDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{expenseToEdit ? 'Edit Expense' : 'Add One-time Expense'}</DialogTitle>
        <DialogContent dividers>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Project (Optional)</InputLabel>
                        <Select value={newExpense.project_id || ""} onChange={(e)=>setNewExpense({...newExpense, project_id: e.target.value})} label="Project (Optional)"> <MenuItem value=""><em>Internal / No Project</em></MenuItem> {projects.map(p => ( <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem> ))} </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}><TextField fullWidth label="Title" value={newExpense.title} onChange={(e)=>setNewExpense({...newExpense, title:e.target.value})} /></Grid>
                <Grid item xs={4}>
                    <TextField select fullWidth label="Currency" value={newExpense.currency} onChange={(e)=>setNewExpense({...newExpense, currency:e.target.value})} InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyIcon fontSize="small" /></InputAdornment> }}> <MenuItem value="MXN">MXN</MenuItem> <MenuItem value="USD">USD</MenuItem> </TextField>
                </Grid>
                <Grid item xs={8}><TextField fullWidth label="Amount" type="number" value={newExpense.amount} onChange={(e)=>setNewExpense({...newExpense, amount:e.target.value})} /></Grid>
                <Grid item xs={6}>
                    <TextField select fullWidth label="Category" value={newExpense.category} onChange={(e)=>setNewExpense({...newExpense, category:e.target.value})}> {EXPENSE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)} </TextField>
                </Grid>
                <Grid item xs={6}><TextField fullWidth type="date" label="Date" value={newExpense.date} onChange={(e)=>setNewExpense({...newExpense, date:e.target.value})} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Payment Status"
                      value={newExpense.payment_status || 'paid'}
                      onChange={(e)=>setNewExpense({...newExpense, payment_status:e.target.value})}
                    >
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="pending">Pending payment</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <ExpenseReceiptPicker files={expenseFiles} onChange={setExpenseFiles} error={expenseUploadError} onError={setExpenseUploadError} />
                    {expenseToEdit && visibleExistingExpenseFiles.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                        {visibleExistingExpenseFiles.map((storedPath, idx) => (
                          <Chip
                            key={`${storedPath}-${idx}`}
                            size="small"
                            label={storedPath.split('/').pop() || `Attachment ${idx + 1}`}
                            onDelete={() => handleRemoveExistingExpenseFile(storedPath)}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    {expenseToEdit && expenseRemovedFiles.length > 0 && (
                      <Button size="small" sx={{ mt: 1 }} onClick={() => setExpenseRemovedFiles([])}>
                        Undo removed files
                      </Button>
                    )}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions><Button onClick={()=>setExpenseDialogOpen(false)}>Cancel</Button><Button variant="contained" disabled={Boolean(expenseUploadError)} onClick={handleSaveExpense}>{expenseToEdit ? 'Update' : 'Save'}</Button></DialogActions>
      </Dialog>

      <Dialog open={fixedExpenseDialogOpen} onClose={() => setFixedExpenseDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Configure Fixed Expense</DialogTitle>
        <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>This creates a template. You will need to click "Record Payment" each month to track it.</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Title (e.g. Rent)" value={newFixedExpense.title} onChange={(e)=>setNewFixedExpense({...newFixedExpense, title:e.target.value})} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Amount" type="number" value={newFixedExpense.amount} onChange={(e)=>setNewFixedExpense({...newFixedExpense, amount:e.target.value})} /></Grid>
                <Grid item xs={6}><TextField select fullWidth label="Category" value={newFixedExpense.category} onChange={(e)=>setNewFixedExpense({...newFixedExpense, category:e.target.value})}> {EXPENSE_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)} </TextField></Grid>
                <Grid item xs={6}><TextField fullWidth label="Day of Month (1-31)" type="number" inputProps={{min:1, max:31}} value={newFixedExpense.day_of_month} onChange={(e)=>setNewFixedExpense({...newFixedExpense, day_of_month:e.target.value})} /></Grid>
            </Grid>
        </DialogContent>
        <DialogActions><Button onClick={()=>setFixedExpenseDialogOpen(false)}>Cancel</Button><Button variant="contained" color="secondary" onClick={handleSaveFixedExpense}>Save Template</Button></DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are about to permanently delete this record.
          </Typography>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, 0.22),
              bgcolor: alpha(theme.palette.error.main, 0.05)
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              RECORD TYPE
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              {deleteTypeLabel}
            </Typography>
            {deleteConfirm?.title && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  IDENTIFIER
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {deleteConfirm.title}
                </Typography>
              </>
            )}
            {deleteConfirm?.subtitle && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  DETAILS
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {deleteConfirm.subtitle}
                </Typography>
              </>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {deleteStatusLabel && (
                <Chip
                  size="small"
                  label={`Status: ${deleteStatusLabel}`}
                  color={deleteConfirm?.type === 'expense' && deleteConfirm?.status !== 'paid' ? 'warning' : 'default'}
                  variant="outlined"
                />
              )}
              {deleteConfirm?.amount !== undefined && deleteConfirm?.amount !== null && (
                <Chip
                  size="small"
                  label={`Amount: ${formatCurrency(deleteConfirm.amount, deleteConfirm.currency || 'MXN')}`}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete Record
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentDialogOpen}
        onClose={() => !paymentSaving && setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="success.main" sx={{ fontWeight: 800 }}>PAYMENT LEDGER</Typography>
              <Typography variant="h6" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>{paymentTarget?.invoice_number}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{paymentTarget?.client_name} / Total {formatCurrency(paymentTarget?.total, paymentTarget?.currency || 'MXN')}</Typography>
            </Box>
            <Chip label={`${paymentLedger.payments.length} installment${paymentLedger.payments.length === 1 ? '' : 's'}`} variant="outlined" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {paymentLedgerLoading ? (
            <Box sx={{ py: 7, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2.2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                    <Typography variant="caption" color="text.secondary">Document total</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 850 }}>{formatCurrency(paymentTarget?.total, paymentTarget?.currency || 'MXN')}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: alpha(theme.palette.success.main, 0.35) }}>
                    <Typography variant="caption" color="text.secondary">Received from customer</Typography>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 850 }}>{formatCurrency(paymentLedger.summary.incoming_paid, paymentTarget?.currency || 'MXN')}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: alpha(theme.palette.warning.main, 0.35) }}>
                    <Typography variant="caption" color="text.secondary">Paid to supplier</Typography>
                    <Typography variant="h6" color="warning.main" sx={{ fontWeight: 850 }}>{formatCurrency(paymentLedger.summary.outgoing_paid, paymentTarget?.currency || 'MXN')}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {canCreateFinanceRecords && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1.5 }}>Register installment</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="Payment flow" value={paymentForm.payment_direction} onChange={(event) => setPaymentForm((current) => ({ ...current, payment_direction: event.target.value }))}>
                        <MenuItem value="incoming">Incoming / Customer paid us</MenuItem>
                        <MenuItem value="outgoing">Outgoing / We paid supplier</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" type="number" label="Amount" value={paymentForm.amount} onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))} inputProps={{ min: 0.01, step: '0.01' }} helperText={`Available balance: ${formatCurrency(paymentForm.payment_direction === 'incoming' ? paymentLedger.summary.incoming_balance : paymentLedger.summary.outgoing_balance, paymentTarget?.currency || 'MXN')}`} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" type="date" label="Payment Date" value={paymentForm.payment_date} onChange={(event) => setPaymentForm((current) => ({ ...current, payment_date: event.target.value }))} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="Method" value={paymentForm.payment_method} onChange={(event) => setPaymentForm((current) => ({ ...current, payment_method: event.target.value }))}>
                        <MenuItem value="Bank transfer">Bank transfer</MenuItem><MenuItem value="Credit card">Credit card</MenuItem><MenuItem value="Cash">Cash</MenuItem><MenuItem value="Check">Check</MenuItem><MenuItem value="Other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Reference" value={paymentForm.reference} onChange={(event) => setPaymentForm((current) => ({ ...current, reference: event.target.value }))} placeholder="Transfer or receipt number" /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Notes" value={paymentForm.notes} onChange={(event) => setPaymentForm((current) => ({ ...current, notes: event.target.value }))} /></Grid>
                  </Grid>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}><Button variant="contained" color="success" startIcon={<PaymentIcon />} onClick={handleSaveDocumentPayment} disabled={paymentSaving || Number(paymentForm.amount) <= 0}>{paymentSaving ? 'Registering...' : 'Register Payment'}</Button></Stack>
                </Paper>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Payment history</Typography>
                {paymentLedger.payments.length === 0 ? (
                  <Alert severity="info">No partial payments have been registered. Finance continues using the legacy document status until the first installment is added.</Alert>
                ) : (
                  <>
                  <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                    {paymentLedger.payments.map((payment) => (
                      <Paper key={`mobile-payment-${payment.id}`} variant="outlined" sx={{ p: 1.4, borderRadius: 2.2 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Box sx={{ minWidth: 0 }}><Chip size="small" label={payment.payment_direction === 'incoming' ? 'Incoming' : 'Outgoing'} color={payment.payment_direction === 'incoming' ? 'success' : 'warning'} variant="outlined" /><Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>{payment.payment_date ? format(new Date(`${String(payment.payment_date).slice(0, 10)}T12:00:00`), 'dd MMM yyyy') : '-'}</Typography></Box>
                          <Typography fontWeight={850}>{formatCurrency(payment.amount, payment.currency)}</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{payment.reference || payment.payment_method || '-'}</Typography>
                        {payment.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{payment.notes}</Typography>}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}><Typography variant="caption" color="text.secondary">By {payment.created_by_name || '-'}</Typography>{canDeleteFinanceRecords && <IconButton size="small" color="error" onClick={() => handleDeleteDocumentPayment(payment)} disabled={paymentSaving}><DeleteIcon fontSize="small" /></IconButton>}</Stack>
                      </Paper>
                    ))}
                  </Stack>
                  <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2.5 }}>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Flow</TableCell><TableCell>Reference</TableCell><TableCell>Registered by</TableCell><TableCell align="right">Amount</TableCell><TableCell /></TableRow></TableHead>
                      <TableBody>{paymentLedger.payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.payment_date ? format(new Date(`${String(payment.payment_date).slice(0, 10)}T12:00:00`), 'dd MMM yyyy') : '-'}</TableCell>
                          <TableCell><Chip size="small" label={payment.payment_direction === 'incoming' ? 'Incoming' : 'Outgoing'} color={payment.payment_direction === 'incoming' ? 'success' : 'warning'} variant="outlined" /></TableCell>
                          <TableCell><Typography variant="body2">{payment.reference || payment.payment_method || '-'}</Typography>{payment.notes && <Typography variant="caption" color="text.secondary">{payment.notes}</Typography>}</TableCell>
                          <TableCell>{payment.created_by_name || '-'}</TableCell>
                          <TableCell align="right"><Typography sx={{ fontWeight: 850 }}>{formatCurrency(payment.amount, payment.currency)}</Typography></TableCell>
                          <TableCell>{canDeleteFinanceRecords && <Tooltip title="Delete payment"><IconButton size="small" color="error" onClick={() => handleDeleteDocumentPayment(payment)} disabled={paymentSaving}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}</TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </TableContainer>
	              </>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPaymentDialogOpen(false)} disabled={paymentSaving}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        {selectedItem && (
          <>
            <DialogTitle>{selectedItem.invoice_number || selectedItem.title}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Client / Vendor</Typography><Typography variant="body1">{selectedItem.client_name || selectedItem.category}</Typography></Grid>
                    {selectedItem.type && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Issuing legal entity</Typography>
                        <Typography variant="body1">
                          {selectedItem.issuer_entity === 'us_llc'
                            ? 'SMTLINK SOLUTIONS LLC — USA'
                            : selectedItem.issuer_entity === 'mx_sa'
                              ? 'SMTLINK SOLUTIONS, S.A. de C.V. — México'
                              : 'Not assigned'}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Date</Typography><Typography variant="body1">{selectedItem.issue_date || selectedItem.date}</Typography></Grid>
                    {Boolean(selectedItem.discount_enabled) && Number(selectedItem.discount_amount) > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Discount ({Number(selectedItem.discount_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%)
                        </Typography>
                        <Typography variant="body1" color="warning.main" sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>
                          -{formatCurrency(selectedItem.discount_amount, selectedItem.currency || 'MXN')}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}><Typography variant="subtitle2" color="text.secondary">Total Amount</Typography><Typography variant="h6" color="primary.main">{formatCurrency(selectedItem.total || selectedItem.amount, selectedItem.currency || 'MXN')}</Typography></Grid>
                </Grid>
            </DialogContent>
            <DialogActions><Button onClick={() => setOpenDialog(false)}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={expenseAttachmentsDialogOpen}
        onClose={() => {
          setExpenseAttachmentsDialogOpen(false);
          setSelectedExpenseAttachmentExpenseId(null);
          setSelectedExpenseAttachmentPreviewPath('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Attachments - {selectedExpenseAttachmentTitle}</DialogTitle>
        <DialogContent dividers>
          {selectedExpenseAttachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No attachments found.</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                  {selectedExpenseAttachments.map((storedPath, index) => {
                    const selected = selectedExpenseAttachmentPreviewPath === storedPath;
                    return (
                      <Paper
                        key={`${storedPath}-${index}`}
                        variant="outlined"
                        sx={{
                          p: 1.2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent'
                        }}
                      >
                        <Button
                          onClick={() => setSelectedExpenseAttachmentPreviewPath(storedPath)}
                          sx={{ minWidth: 0, p: 0, textTransform: 'none', justifyContent: 'flex-start', mr: 1, flex: 1 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <ReceiptIcon fontSize="small" />
                            <Typography variant="body2" noWrap>
                              {storedPath.split('/').pop() || `Attachment ${index + 1}`}
                            </Typography>
                          </Box>
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleOpenExpenseAttachment(selectedExpenseAttachmentExpenseId, index, storedPath)}
                        >
                          Open
                        </Button>
                      </Paper>
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                <Paper variant="outlined" sx={{ height: 420, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  {!selectedExpenseAttachmentPreviewPath ? (
                    <Typography variant="body2" color="text.secondary">Select an attachment to preview.</Typography>
                  ) : selectedExpenseAttachmentPreviewLoading ? (
                    <CircularProgress size={28} />
                  ) : !selectedExpenseAttachmentPreviewUrl ? (
                    <Box sx={{ textAlign: 'center', px: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        A preview of this attachment could not be loaded.
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          const selectedIndex = selectedExpenseAttachments.findIndex((storedPath) => storedPath === selectedExpenseAttachmentPreviewPath);
                          if (selectedIndex >= 0) handleOpenExpenseAttachment(
                            selectedExpenseAttachmentExpenseId,
                            selectedIndex,
                            selectedExpenseAttachmentPreviewPath
                          );
                        }}
                      >
                        Open File
                      </Button>
                    </Box>
                  ) : isImageAttachment(selectedExpenseAttachmentPreviewPath) ? (
                    <Box
                      component="img"
                      src={selectedExpenseAttachmentPreviewUrl}
                      alt="Expense attachment preview"
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
                    />
                  ) : isPdfAttachment(selectedExpenseAttachmentPreviewPath) ? (
                    <Box
                      component="iframe"
                      src={selectedExpenseAttachmentPreviewUrl}
                      title="Attachment PDF Preview"
                      sx={{ width: '100%', height: '100%', border: 'none', borderRadius: 1, bgcolor: '#fff' }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', px: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Preview is not available for this file type.
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          const selectedIndex = selectedExpenseAttachments.findIndex((storedPath) => storedPath === selectedExpenseAttachmentPreviewPath);
                          if (selectedIndex >= 0) handleOpenExpenseAttachment(
                            selectedExpenseAttachmentExpenseId,
                            selectedIndex,
                            selectedExpenseAttachmentPreviewPath
                          );
                        }}
                      >
                        Open File
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExpenseAttachmentsDialogOpen(false);
            setSelectedExpenseAttachmentExpenseId(null);
            setSelectedExpenseAttachmentPreviewPath('');
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusHistoryOpen}
        onClose={() => setStatusHistoryOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Status History {statusHistoryInvoice?.invoice_number ? `- ${statusHistoryInvoice.invoice_number}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {statusHistoryLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!statusHistoryLoading && statusHistoryError && (
            <Alert severity="error">{statusHistoryError}</Alert>
          )}

          {!statusHistoryLoading && !statusHistoryError && statusHistoryRows.length === 0 && (
            <Alert severity="info">No status changes have been recorded for this document yet.</Alert>
          )}

          {!statusHistoryLoading && !statusHistoryError && statusHistoryRows.length > 0 && (
            <>
            <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
              {statusHistoryRows.map((entry) => {
                const entryType = entry.to_type || entry.from_type || statusHistoryInvoice?.type || 'invoice';
                const fromLabel = entry.from_status ? getStatusConfigByType(entryType, entry.from_status, statusLabelsByType).label : '-';
                const toLabel = entry.to_status ? getStatusConfigByType(entryType, entry.to_status, statusLabelsByType).label : '-';
                const changedAt = entry.changed_at ? new Date(entry.changed_at) : null;
                const changedAtLabel = changedAt && !Number.isNaN(changedAt.getTime()) ? format(changedAt, 'dd MMM yyyy HH:mm') : '-';
                return (
                  <Paper key={`mobile-status-history-${entry.id}`} variant="outlined" sx={{ p: 1.4, borderRadius: 2.2 }}>
                    <Typography variant="caption" color="text.secondary">{changedAtLabel} • {entry.changed_by || '-'}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}><Chip size="small" variant="outlined" label={fromLabel} /><Typography color="text.secondary">→</Typography><Chip size="small" color="primary" label={toLabel} /></Stack>
                  </Paper>
                );
              })}
            </Stack>
            <TableContainer component={Paper} elevation={0} sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusHistoryRows.map((entry) => {
                    const entryType = entry.to_type || entry.from_type || statusHistoryInvoice?.type || 'invoice';
                    const fromLabel = entry.from_status
                      ? getStatusConfigByType(entryType, entry.from_status, statusLabelsByType).label
                      : '-';
                    const toLabel = entry.to_status
                      ? getStatusConfigByType(entryType, entry.to_status, statusLabelsByType).label
                      : '-';
                    const changedAt = entry.changed_at ? new Date(entry.changed_at) : null;
                    const changedAtLabel = changedAt && !Number.isNaN(changedAt.getTime())
                      ? format(changedAt, 'dd MMM yyyy HH:mm')
                      : '-';

                    return (
                      <TableRow key={entry.id}>
                        <TableCell>{changedAtLabel}</TableCell>
                        <TableCell>{entry.changed_by || '-'}</TableCell>
                        <TableCell>{fromLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{toLabel}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
	        </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notification} autoHideDuration={5000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification(null)} severity={notification?.severity || 'success'} sx={{ width: '100%' }}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
